/**
 * Bundle Product - Custom Elements Architecture
 * 
 * Components:
 * - <bundle-product> - Main controller, manages state and coordinates child components
 * - <bundle-plan-picker> - Handles plan tier selection (filters siblings)
 * - <bundle-sibling-picker> - Handles sibling product selection (swaps product)
 * - <bundle-gallery> - Handles the Swiper gallery with dynamic media updates
 * 
 * Communication: Components communicate via CustomEvents bubbling up to <bundle-product>
 */

import Swiper from 'swiper/bundle';

// ============================================================================
// DEBUG MODE - Set to true to enable console logging
// ============================================================================
const DEBUG_MODE = false;

// Debug logger utilities - only log when DEBUG_MODE is true
const debugLog = (...args) => DEBUG_MODE && console.log(...args);
const debugWarn = (...args) => DEBUG_MODE && console.warn(...args);
const debugError = (...args) => console.error(...args); // Always show errors

// ============================================================================
// BUNDLE PRODUCT - Main Controller
// ============================================================================
class BundleProduct extends HTMLElement {
  constructor() {
    super();
    
    // State
    this.state = {
      productId: null,
      sectionId: null,
      currentPlanId: null,
      isLoading: false
    };
    
    // Bound methods for event listeners
    this.handlePlanChange = this.handlePlanChange.bind(this);
    this.handleSiblingChange = this.handleSiblingChange.bind(this);
  }

  connectedCallback() {
    // Initialize state from attributes
    this.state.productId = this.dataset.productId;
    this.state.sectionId = this.dataset.sectionId;
    
    // Listen for child component events
    this.addEventListener('bundle:plan-change', this.handlePlanChange);
    this.addEventListener('bundle:sibling-change', this.handleSiblingChange);
    
    debugLog('[BundleProduct] Connected:', this.state);

    // Replace the "*" price placeholder with the computed bundle total
    this.initBundlePrice();
  }

  /**
   * Parse bundle-items-data JSON, sum each item's first variant compareAtPrice,
   * convert from cents to USD, and replace the "*" placeholder in the price text.
   */
  initBundlePrice() {
    const dataEl = document.getElementById('bundle-items-data');
    if (!dataEl) {
      debugWarn('[BundleProduct] No bundle-items-data found for price init');
      return;
    }

    try {
      const data = JSON.parse(dataEl.textContent);
      const { items, roundCents } = data;

      if (!items || !items.length) {
        debugWarn('[BundleProduct] No items found in bundle data');
        return;
      }

      // Sum the compareAtPrice of the first variant from each item (values are in cents)
      let totalCents = 0;
      for (const item of items) {
        if (item.variants && item.variants.length > 0) {
          totalCents += Number(item.variants[0].variantCompareAtPrice) || 0;
        }
      }

      // Convert cents to dollars and format as USD
      let formattedPrice;
      if (roundCents) {
        // Round to nearest dollar, no trailing cents
        const dollars = Math.round(totalCents / 100);
        formattedPrice = '$' + dollars;
      } else {
        // Full price with cents
        formattedPrice = '$' + (totalCents / 100).toFixed(2);
      }

      const priceEl = this.querySelector('.bundle-product__price-text');
      if (priceEl) {
        priceEl.innerHTML = priceEl.innerHTML.replace('*', formattedPrice);
        debugLog('[BundleProduct] Bundle price updated to:', formattedPrice, '(totalCents:', totalCents, ')');
      }
    } catch (e) {
      debugError('[BundleProduct] Error initializing bundle price:', e);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('bundle:plan-change', this.handlePlanChange);
    this.removeEventListener('bundle:sibling-change', this.handleSiblingChange);
  }

  /**
   * Handle plan tier change - fetches new plan product via Section Rendering API
   * @param {CustomEvent} event 
   */
  async handlePlanChange(event) {
    const { planId, collectionHandle, pageUrl } = event.detail;
    debugLog('[BundleProduct] Plan changed:', planId, collectionHandle, pageUrl);
    
    // If no pageUrl or same page, just update state
    if (!pageUrl) {
      debugLog('[BundleProduct] No pageUrl provided, skipping navigation');
      this.state.currentPlanId = planId;
      return;
    }
    
    // Check if we're already on this page
    const currentPath = window.location.pathname;
    const newPath = new URL(pageUrl, window.location.origin).pathname;
    
    if (currentPath === newPath) {
      debugLog('[BundleProduct] Already on this plan page, skipping');
      this.state.currentPlanId = planId;
      return;
    }
    
    // Use the same AJAX approach as sibling picker for consistency
    try {
      this.setLoading(true);
      
      // Get bundle-items section ID if it exists on the page
      const bundleItemsSection = document.querySelector('bundle-items');
      const bundleItemsSectionId = bundleItemsSection?.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      
      // Build section IDs to fetch
      const sectionIds = [this.state.sectionId];
      if (bundleItemsSectionId) {
        sectionIds.push(bundleItemsSectionId);
      }
      
      // Fetch new section HTML using Section Rendering API
      const sectionsHtml = await this.fetchProductSections(pageUrl, sectionIds);
      
      // Update bundle-product section
      if (sectionsHtml[this.state.sectionId]) {
        this.updateSection(sectionsHtml[this.state.sectionId]);
      }
      
      // Update bundle-items section if it exists
      if (bundleItemsSectionId && sectionsHtml[bundleItemsSectionId]) {
        this.updateBundleItemsSection(bundleItemsSectionId, sectionsHtml[bundleItemsSectionId]);
      }
      
      // Update browser URL without reload
      this.updateUrl(pageUrl);
      
      // Update state
      this.state.currentPlanId = planId;
      
      debugLog('[BundleProduct] Plan page updated via AJAX');
      
    } catch (error) {
      debugError('[BundleProduct] Error fetching plan product:', error);
      // Fallback to traditional navigation
      window.location.href = pageUrl;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Handle sibling product change - fetches new product data via Section Rendering API
   * @param {CustomEvent} event 
   */
  async handleSiblingChange(event) {
    const { productUrl, productId } = event.detail;
    
    if (productId === this.state.productId) {
      debugLog('[BundleProduct] Same product selected, skipping');
      return;
    }

    debugLog('[BundleProduct] Sibling changed:', productUrl);
    
    try {
      this.setLoading(true);
      
      // Get bundle-items section ID if it exists on the page
      const bundleItemsSection = document.querySelector('bundle-items');
      const bundleItemsSectionId = bundleItemsSection?.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      
      // Build section IDs to fetch
      const sectionIds = [this.state.sectionId];
      if (bundleItemsSectionId) {
        sectionIds.push(bundleItemsSectionId);
      }
      
      // Fetch new section HTML using Section Rendering API
      const sectionsHtml = await this.fetchProductSections(productUrl, sectionIds);
      
      // Update bundle-product section
      if (sectionsHtml[this.state.sectionId]) {
        this.updateSection(sectionsHtml[this.state.sectionId]);
      }
      
      // Update bundle-items section if it exists
      if (bundleItemsSectionId && sectionsHtml[bundleItemsSectionId]) {
        this.updateBundleItemsSection(bundleItemsSectionId, sectionsHtml[bundleItemsSectionId]);
      }
      
      // Update browser URL without reload
      this.updateUrl(productUrl);
      
      // Update state
      this.state.productId = productId;
      
    } catch (error) {
      debugError('[BundleProduct] Error fetching product:', error);
      // Fallback to traditional navigation
      window.location.href = productUrl;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetch section HTML via Section Rendering API
   * @param {string} productUrl 
   * @returns {Promise<string>}
   */
  async fetchProductSection(productUrl) {
    const sectionId = this.state.sectionId;
    const url = `${productUrl}?section_id=${sectionId}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch section: ${response.status}`);
    }
    
    return response.text();
  }

  /**
   * Fetch multiple sections HTML via Section Rendering API
   * @param {string} productUrl 
   * @param {string[]} sectionIds 
   * @returns {Promise<Object>} Object with section IDs as keys and HTML as values
   */
  async fetchProductSections(productUrl, sectionIds) {
    const url = `${productUrl}?sections=${sectionIds.join(',')}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch sections: ${response.status}`);
    }
    
    return response.json();
  }

  /**
   * Update the section DOM with new HTML
   * @param {string} html 
   */
  updateSection(html) {
    // Parse the new HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newSection = doc.querySelector('.bundle-product');
    
    if (!newSection) {
      throw new Error('Could not find .bundle-product in response');
    }

    // Get references to elements we need to update
    const infoTop = this.querySelector('.bundle-product__info-top');
    const gallery = this.querySelector('.bundle-product__gallery');
    const info = this.querySelector('.bundle-product__info');
    
    const newInfoTop = newSection.querySelector('.bundle-product__info-top');
    const newGallery = newSection.querySelector('.bundle-product__gallery');
    const newInfo = newSection.querySelector('.bundle-product__info');

    // Update info-top section (plan picker, title, description, price)
    if (infoTop && newInfoTop) {
      infoTop.innerHTML = newInfoTop.innerHTML;
      debugLog('[BundleProduct] Info-top section updated');
    }

    // Update gallery - destroy swiper BEFORE updating innerHTML
    if (gallery && newGallery) {
      // Destroy existing swiper instance before replacing content
      const swiperEl = gallery.querySelector('.swiper');
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
      }
      
      // Copy data attributes from new gallery to existing gallery
      Array.from(newGallery.attributes).forEach(attr => {
        if (attr.name.startsWith('data-')) {
          gallery.setAttribute(attr.name, attr.value);
        }
      });
      
      // Now update the content
      gallery.innerHTML = newGallery.innerHTML;
      
      // Re-initialize swiper after a microtask to ensure DOM is ready
      requestAnimationFrame(() => {
        this.reinitializeGallery();
      });
    }

    // Update info section
    // Custom element lifecycle handles swiper init/destroy:
    // - Old <bundle-sibling-picker> removed → disconnectedCallback destroys swiper
    // - New <bundle-sibling-picker> added → connectedCallback initializes swiper
    if (info && newInfo) {
      info.innerHTML = newInfo.innerHTML;
    }

    // Update bundle-items-data script tag (lives outside <bundle-product> in section HTML)
    const newDataScript = doc.getElementById('bundle-items-data');
    if (newDataScript) {
      const existingDataScript = document.getElementById('bundle-items-data');
      if (existingDataScript) {
        existingDataScript.textContent = newDataScript.textContent;
      }
    }

    // Update alternates data and modal (both live outside <bundle-product>)
    const newAlternatesData = doc.getElementById('bundle-alternates-data');
    if (newAlternatesData) {
      const existingAlternatesData = document.getElementById('bundle-alternates-data');
      if (existingAlternatesData) {
        existingAlternatesData.textContent = newAlternatesData.textContent;
      }
    }

    // Replace the alternates modal with the fresh version from the response
    // This triggers disconnectedCallback on the old element and connectedCallback
    // on the new one, which re-parses data, re-caches options, and re-registers
    const newModal = doc.querySelector('bundle-alternates-modal');
    const existingModal = document.querySelector('bundle-alternates-modal');
    if (newModal && existingModal) {
      existingModal.outerHTML = newModal.outerHTML;
      debugLog('[BundleProduct] Alternates modal replaced with fresh HTML');
    }

    // Sync all data attributes from the new section to this element.
    // This ensures productId, variantId, productTitle, bundlePrice, plan attributes, etc. are current.
    // First remove old data- attrs that may no longer exist on the new section (e.g. plan attrs).
    const newDataAttrs = new Set(
      Array.from(newSection.attributes).filter(a => a.name.startsWith('data-')).map(a => a.name)
    );
    Array.from(this.attributes).forEach(attr => {
      if (attr.name.startsWith('data-') && !newDataAttrs.has(attr.name)) {
        this.removeAttribute(attr.name);
      }
    });
    // Then copy all data- attrs from the new section
    Array.from(newSection.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        this.setAttribute(attr.name, attr.value);
      }
    });

    // Re-run price initialization with the updated bundle data
    this.initBundlePrice();
    
    // Dispatch event for any listeners
    this.dispatchEvent(new CustomEvent('bundle:product-updated', {
      detail: { productId: newSection.dataset.productId },
      bubbles: true
    }));
  }

  /**
   * Update the bundle-items section DOM with new HTML
   * @param {string} sectionId 
   * @param {string} html 
   */
  updateBundleItemsSection(sectionId, html) {
    const sectionContainer = document.getElementById(`shopify-section-${sectionId}`);
    
    if (!sectionContainer) {
      debugWarn('[BundleProduct] Could not find bundle-items section container');
      return;
    }

    // Parse the new HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newBundleItems = doc.querySelector('bundle-items');
    
    if (!newBundleItems) {
      debugWarn('[BundleProduct] Could not find bundle-items in response');
      return;
    }

    // Get existing bundle-items element
    const existingBundleItems = sectionContainer.querySelector('bundle-items');
    
    if (existingBundleItems) {
      // Replace the entire bundle-items element
      // disconnectedCallback on the old element will destroy the old Swiper,
      // and connectedCallback on the new element will initialize a fresh one.
      existingBundleItems.outerHTML = newBundleItems.outerHTML;
      
      // Safety net: ensure Swiper is initialized after layout settles
      const updatedBundleItems = sectionContainer.querySelector('bundle-items');
      if (updatedBundleItems) {
        requestAnimationFrame(() => {
          this.reinitializeBundleItemsSwiper(updatedBundleItems);
        });
      }
    }
  }

  /**
   * Re-initialize the Swiper for bundle-items after DOM update.
   * Delegates to the BundleItems element's own initSwiper method.
   * @param {HTMLElement} bundleItems 
   */
  reinitializeBundleItemsSwiper(bundleItems) {
    if (typeof bundleItems.initSwiper === 'function') {
      bundleItems.initSwiper();
    }
  }

  /**
   * Re-initialize the Swiper gallery after DOM update
   */
  reinitializeGallery() {
    const gallery = this.querySelector('.bundle-product__gallery');
    if (!gallery) return;

    const swiperEl = gallery.querySelector('.swiper');
    if (!swiperEl) return;
    
    // Get options from the gallery's data attributes
    const options = this.getSwiperOptionsFromGallery(gallery);
    
    // Initialize new Swiper directly
    new Swiper(swiperEl, options);
  }

  /**
   * Extract Swiper options from gallery data attributes
   * @param {HTMLElement} gallery 
   * @returns {Object}
   */
  getSwiperOptionsFromGallery(gallery) {
    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 300,
      a11y: true
    };

    // Parse data attributes
    if (gallery.dataset.slidesPerView) {
      options.slidesPerView = parseFloat(gallery.dataset.slidesPerView) || 1;
    }
    if (gallery.dataset.spaceBetween) {
      options.spaceBetween = parseInt(gallery.dataset.spaceBetween) || 0;
    }
    if (gallery.dataset.speed) {
      options.speed = parseInt(gallery.dataset.speed) || 300;
    }
    if (gallery.dataset.loop === 'true') {
      options.loop = true;
    }

    // Pagination
    if (gallery.dataset.pagination !== 'false') {
      const paginationEl = gallery.querySelector('[class*="__pagination"]') || gallery.querySelector('.swiper-pagination');
      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
          type: gallery.dataset.paginationType || 'bullets'
        };
      }
    }

    // Navigation
    if (gallery.dataset.navigation !== 'false') {
      const prevBtn = gallery.querySelector('.bundle-product__button--prev');
      const nextBtn = gallery.querySelector('.bundle-product__button--next');
      if (prevBtn && nextBtn) {
        options.navigation = {
          prevEl: prevBtn,
          nextEl: nextBtn
        };
      }
    }

    // Observer for dynamic content
    if (gallery.dataset.observer === 'true') {
      options.observer = true;
      options.observeParents = true;
    }

    return options;
  }

  /**
   * Update browser URL without page reload
   * @param {string} url 
   */
  updateUrl(url) {
    history.pushState({ productUrl: url }, '', url);
  }

  /**
   * Set loading state
   * @param {boolean} isLoading 
   */
  setLoading(isLoading) {
    this.state.isLoading = isLoading;
    this.classList.toggle('is-loading', isLoading);
    
    // Dispatch loading event for UI feedback
    this.dispatchEvent(new CustomEvent('bundle:loading', {
      detail: { isLoading },
      bubbles: true
    }));
  }
}


// ============================================================================
// BUNDLE PLAN PICKER - Handles plan tier selection
// ============================================================================
class BundlePlanPicker extends HTMLElement {
  constructor() {
    super();
    this.buttons = [];
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.buttons = this.querySelectorAll('.bundle-product__plan-picker-option-button');
    
    this.buttons.forEach(button => {
      button.addEventListener('click', this.handleClick);
    });

    // Set initial active state
    this.setActiveFromUrl();
  }

  disconnectedCallback() {
    this.buttons.forEach(button => {
      button.removeEventListener('click', this.handleClick);
    });
  }

  /**
   * Handle plan button click
   * @param {Event} event 
   */
  handleClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const planId = button.dataset.planId;
    const collectionHandle = button.dataset.collectionHandle;
    const pageUrl = button.dataset.pageUrl;

    // Update active state immediately for responsiveness
    this.setActive(button);

    // Dispatch event to parent with pageUrl for AJAX navigation
    this.dispatchEvent(new CustomEvent('bundle:plan-change', {
      detail: { planId, collectionHandle, pageUrl },
      bubbles: true
    }));
  }

  /**
   * Set active button
   * @param {HTMLElement} activeButton 
   */
  setActive(activeButton) {
    this.buttons.forEach(btn => {
      btn.classList.toggle('is-active', btn === activeButton);
      btn.setAttribute('aria-pressed', btn === activeButton ? 'true' : 'false');
    });
  }

  /**
   * Set active state based on current URL/product
   */
  setActiveFromUrl() {
    // Implementation depends on how you want to determine the active plan
    // Could check data attributes against current product metafields
  }
}


// ============================================================================
// BUNDLE SIBLING PICKER - Handles sibling product selection
// ============================================================================
class BundleSiblingPicker extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.swiper = null;
    this.handleClick = this.handleClick.bind(this);
    this.handleFilterSiblings = this.handleFilterSiblings.bind(this);
  }

  connectedCallback() {
    this.options = this.querySelectorAll('.bundle-product__sibling-option');
    
    // Attach click listeners
    this.options.forEach(option => {
      option.addEventListener('click', this.handleClick);
    });

    // Listen for filter events from parent
    const bundleProduct = this.closest('bundle-product');
    if (bundleProduct) {
      bundleProduct.addEventListener('bundle:filter-siblings', this.handleFilterSiblings);
    }

    // Initialize swiper if needed (after a brief delay for DOM/CSS readiness)
    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
    
    this.options.forEach(option => {
      option.removeEventListener('click', this.handleClick);
    });

    const bundleProduct = this.closest('bundle-product');
    if (bundleProduct) {
      bundleProduct.removeEventListener('bundle:filter-siblings', this.handleFilterSiblings);
    }
  }

  /**
   * Initialize Swiper for sibling picker carousel
   */
  initSwiper() {
    // Guard against double initialization
    if (this.swiper) return;
    
    const swiperContainer = this.querySelector('[data-sibling-swiper="true"]');
    if (!swiperContainer) return;

    const swiperEl = swiperContainer.querySelector('.swiper');
    if (!swiperEl) return;
    
    // Also check if swiper is already initialized on the element
    if (swiperEl.swiper) return;

    // Wait for next frame to ensure CSS is applied
    requestAnimationFrame(() => {
      // Double-check container has width before initializing
      if (swiperContainer.offsetWidth === 0) {
        // Retry after a short delay if container not ready
        setTimeout(() => this.initSwiper(), 50);
        return;
      }

      const slidesPerView = parseInt(swiperContainer.dataset.slidesPerView) || 3;
      const spaceBetween = parseInt(swiperContainer.dataset.spaceBetween) || 12;

      // Find pagination element
      const paginationEl = swiperContainer.querySelector('.bundle-product__sibling-picker-pagination');

      this.swiper = new Swiper(swiperEl, {
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        speed: 300,
        grabCursor: true,
        watchSlidesProgress: true,
        // Resize handling
        observer: true,
        observeParents: true,
        resizeObserver: true,
        pagination: paginationEl ? {
          el: paginationEl,
          clickable: true,
          type: 'bullets'
        } : false
      });
    });
  }

  /**
   * Destroy Swiper instance
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Handle sibling option click
   * @param {Event} event 
   */
  handleClick(event) {
    event.preventDefault();
    const option = event.currentTarget;
    const productUrl = option.href;
    const productId = option.dataset.siblingId;

    // Update active state immediately for responsiveness
    this.setActive(option);

    // Dispatch event to parent
    this.dispatchEvent(new CustomEvent('bundle:sibling-change', {
      detail: { productUrl, productId },
      bubbles: true
    }));
  }

  /**
   * Filter visible siblings by collection
   * @param {CustomEvent} event 
   */
  handleFilterSiblings(event) {
    const { collectionHandle } = event.detail;
    
    this.options.forEach(option => {
      const optionCollection = option.dataset.collectionHandle;
      const shouldShow = !collectionHandle || optionCollection === collectionHandle;
      option.closest('.bundle-product__plan-picker-option')?.classList.toggle('is-hidden', !shouldShow);
    });
  }

  /**
   * Set active option
   * @param {HTMLElement} activeOption 
   */
  setActive(activeOption) {
    this.options.forEach(opt => {
      const isActive = opt === activeOption;
      opt.classList.toggle('is-active', isActive);
      opt.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
  }
}


// ============================================================================
// BUNDLE GALLERY - Handles Swiper gallery with dynamic updates
// ============================================================================
class BundleGallery extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    // Gallery initialization is handled by swiper-init.js
    // This component can handle gallery-specific logic
    
    // Listen for product updates to reinitialize
    const bundleProduct = this.closest('bundle-product');
    if (bundleProduct) {
      bundleProduct.addEventListener('bundle:product-updated', () => {
        this.reinitialize();
      });
    }
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  /**
   * Reinitialize the gallery swiper
   */
  reinitialize() {
    this.destroySwiper();
    
    // Use your existing swiper init system
    if (typeof window.reinitSwipers === 'function') {
      window.reinitSwipers(this);
    }
  }

  /**
   * Destroy current swiper instance
   */
  destroySwiper() {
    const swiperEl = this.querySelector('.swiper');
    if (swiperEl?.swiper) {
      swiperEl.swiper.destroy(true, true);
    }
  }

  /**
   * Update gallery media programmatically
   * @param {Array} media - Array of media objects
   */
  updateMedia(media) {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Clear existing slides
    wrapper.innerHTML = '';

    // Add new slides
    media.forEach(item => {
      const slide = this.createSlide(item);
      wrapper.appendChild(slide);
    });

    // Reinitialize swiper
    this.reinitialize();
  }

  /**
   * Create a slide element from media data
   * @param {Object} mediaItem 
   * @returns {HTMLElement}
   */
  createSlide(mediaItem) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.dataset.mediaId = mediaItem.id;

    if (mediaItem.media_type === 'image') {
      const img = document.createElement('img');
      img.src = mediaItem.src;
      img.alt = mediaItem.alt || '';
      img.loading = 'lazy';

      // Detect aspect-ratio mismatch on load — apply fit-contain for non-1:1 images
      img.onload = () => {
        const imgRatio = img.naturalWidth / img.naturalHeight;
        const containerRatio = 4 / 5; // 4:5 container
        if (Math.abs(imgRatio - containerRatio) > 0.1) {
          img.classList.add('fit-contain');
        }
      };

      slide.appendChild(img);
    }
    // Add handling for video/external_video as needed

    return slide;
  }
}


// ============================================================================
// BUNDLE VARIANT SELECTS - Category-Aware Variant Selection and Matching
// ============================================================================
class BundleVariantSelects extends HTMLElement {
  // sessionStorage key for bundle selections
  static STORAGE_KEY = 'bundle_selections';

  constructor() {
    super();
    
    // Category groups data (populated from JSON)
    this.categoryGroups = new Map();
    this.shoeGroup = null;
    
    // Valid waist×inseam combinations per category (for cross-validation)
    // Structure: { categoryKey: Set(['29x30', '30x30', '30x32', ...]) }
    this.validCombinations = new Map();
    
    // Valid waist×inseam combinations PER TYPE for type cross-validation
    // Structure: { categoryKey: Map({ type: Set(['29x30', '30x30', ...]) }) }
    this.validCombinationsByType = new Map();
    
    // User selections keyed by categoryKey
    // Structure: { categoryKey: { type, size } } or { categoryKey: { type, waist, inseam } }
    this.selections = new Map();
    
    // Selected variants for each product (keyed by productId)
    this.selectedVariants = new Map();
    
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    debugLog('%c[BundleVariantSelects] ========== CONNECTED ==========', 'color: green; font-weight: bold; font-size: 14px;');
    debugLog('[BundleVariantSelects] Element:', this);
    debugLog('[BundleVariantSelects] Looking for script[data-category-groups]...');
    
    const scriptEl = this.querySelector('script[data-category-groups]');
    debugLog('[BundleVariantSelects] Script element found:', scriptEl ? 'YES' : 'NO');
    if (scriptEl) {
      debugLog('[BundleVariantSelects] Script content length:', scriptEl.textContent?.length || 0);
    }
    
    // Parse category groups data from JSON
    this.parseCategoryGroupsData();
    
    // Listen for radio/select changes
    this.addEventListener('change', this.handleChange);
    
    // Setup info tooltip click-to-toggle for mobile/touch
    this.setupInfoTooltips();
    
    // Load saved selections from sessionStorage
    this.loadSavedSelections();
    
    // Initialize with any pre-selected values (from DOM or sessionStorage)
    this.initializeSelections();
    
    // Listen for swap events from the alternates modal
    this.boundHandleSwapConfirmed = (event) => {
      const { original, replacement } = event.detail;
      this.handleSwapConfirmed(original, replacement);
    };
    this.boundHandleSwapsComplete = (event) => {
      const { swaps } = event.detail;
      this.handleSwapsComplete(swaps);
    };
    
    document.addEventListener('bundle:swap-confirmed', this.boundHandleSwapConfirmed);
    document.addEventListener('bundle:swaps-complete', this.boundHandleSwapsComplete);
    
    debugLog('[BundleVariantSelects] ========== INIT COMPLETE ==========');
    debugLog('[BundleVariantSelects] Category groups:', Array.from(this.categoryGroups.entries()));
    debugLog('[BundleVariantSelects] Shoe group:', this.shoeGroup);
    debugLog('[BundleVariantSelects] Current selections:', Object.fromEntries(this.selections));
    debugLog('[BundleVariantSelects] Selected variants:', Array.from(this.selectedVariants.entries()));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    
    if (this.boundHandleSwapConfirmed) {
      document.removeEventListener('bundle:swap-confirmed', this.boundHandleSwapConfirmed);
    }
    if (this.boundHandleSwapsComplete) {
      document.removeEventListener('bundle:swaps-complete', this.boundHandleSwapsComplete);
    }
    
    // Clean up info tooltip listeners
    this.cleanupInfoTooltips();
  }

  /**
   * Setup click-to-toggle for info tooltips (for mobile/touch)
   * Hover still works on desktop, but click toggles aria-expanded for touch devices
   */
  setupInfoTooltips() {
    this.infoTriggers = this.querySelectorAll('.bundle-product__fieldset-info-trigger');
    
    this.infoTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleInfoTriggerClick);
    });
    
    // Close tooltips when clicking outside
    this.boundCloseTooltips = (event) => {
      if (!event.target.closest('.bundle-product__fieldset-info-trigger')) {
        this.infoTriggers.forEach(trigger => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }
    };
    document.addEventListener('click', this.boundCloseTooltips);
  }

  /**
   * Handle info trigger click - toggle tooltip visibility
   * @param {Event} event 
   */
  handleInfoTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const trigger = event.currentTarget;
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
  }

  /**
   * Clean up info tooltip event listeners
   */
  cleanupInfoTooltips() {
    if (this.infoTriggers) {
      this.infoTriggers.forEach(trigger => {
        trigger.removeEventListener('click', this.handleInfoTriggerClick);
      });
    }
    
    if (this.boundCloseTooltips) {
      document.removeEventListener('click', this.boundCloseTooltips);
    }
  }

  /**
   * Get the bundle product ID
   * @returns {string}
   */
  getBundleProductId() {
    return this.closest('bundle-product')?.dataset.productId || '';
  }

  /**
   * Parse category groups data from the JSON script tag
   */
  parseCategoryGroupsData() {
    debugLog('[BundleVariantSelects] Parsing category groups data...');
    
    const dataEl = this.querySelector('script[data-category-groups]');
    
    if (!dataEl) {
      debugError('[BundleVariantSelects] ❌ No script[data-category-groups] element found!');
      debugError('[BundleVariantSelects] Make sure bundle-variant-picker.liquid is rendered');
      return;
    }
    
    const rawContent = dataEl.textContent || '';
    debugLog('[BundleVariantSelects] Raw JSON length:', rawContent.length);
    
    // Fix common JSON issues from Liquid templating
    // Remove leading comma after [ in categoryGroups array (Liquid bug workaround)
    let cleanedContent = rawContent.replace(/\[\s*,\s*\{/g, '[{');
    
    // Also fix any trailing commas before ] (another common Liquid issue)
    cleanedContent = cleanedContent.replace(/,\s*\]/g, ']');
    
    try {
      const data = JSON.parse(cleanedContent);
      
      debugLog('[BundleVariantSelects] Parsed JSON:', data);
      debugLog('[BundleVariantSelects] categoryGroups in JSON:', data.categoryGroups?.length || 0);
      debugLog('[BundleVariantSelects] shoeGroup in JSON:', data.shoeGroup ? 'yes' : 'no');
      
      // Parse category groups
      if (data.categoryGroups && Array.isArray(data.categoryGroups)) {
        data.categoryGroups.forEach(group => {
          if (group.categoryKey) {
            this.categoryGroups.set(group.categoryKey, group);
            debugLog(`[BundleVariantSelects] Added category: ${group.categoryKey} (${group.category})`);
          } else {
            debugWarn('[BundleVariantSelects] Skipping group without categoryKey:', group);
          }
        });
      } else {
        debugError('[BundleVariantSelects] ❌ categoryGroups is missing or not an array in JSON');
      }
      
      // Parse shoe group
      if (data.shoeGroup) {
        this.shoeGroup = data.shoeGroup;
        debugLog('[BundleVariantSelects] Added shoe group with', data.shoeGroup.products?.length || 0, 'products');
      }
      
      // Build valid combinations map for waist-inseam categories
      this.buildValidCombinationsMap();
      
      debugLog('[BundleVariantSelects] ✓ Category groups count:', this.categoryGroups.size);
      debugLog('[BundleVariantSelects] ✓ Category keys:', Array.from(this.categoryGroups.keys()));
    } catch (e) {
      debugError('[BundleVariantSelects] ❌ Error parsing category groups JSON:', e);
      debugError('[BundleVariantSelects] Raw JSON (first 500 chars):', rawContent.substring(0, 500));
      debugError('[BundleVariantSelects] Cleaned JSON (first 500 chars):', cleanedContent.substring(0, 500));
    }
  }

  /**
   * Build valid waist×inseam combinations map from variant data
   * Extracts all valid size combinations from variants for cross-validation
   * Also builds per-type combinations for type cross-validation
   */
  buildValidCombinationsMap() {
    this.categoryGroups.forEach((group, categoryKey) => {
      if (group.sizingType !== 'waist-inseam') return;
      
      const validCombos = new Set();
      const combosByType = new Map();
      
      // Iterate through all products in this category
      group.products?.forEach(product => {
        const productType = product.type;
        
        // Initialize Set for this type if not exists
        if (productType && !combosByType.has(productType)) {
          combosByType.set(productType, new Set());
        }
        
        product.variants?.forEach(variant => {
          // Extract waist×inseam from variant options or title
          const combo = this.extractWaistInseamFromVariant(variant);
          if (combo) {
            validCombos.add(combo);
            
            // Also add to type-specific set
            if (productType) {
              combosByType.get(productType).add(combo);
            }
          }
        });
      });
      
      this.validCombinations.set(categoryKey, validCombos);
      this.validCombinationsByType.set(categoryKey, combosByType);
      
      debugLog(`[BundleVariantSelects] Valid combos for ${categoryKey}:`, Array.from(validCombos));
      debugLog(`[BundleVariantSelects] Combos by type for ${categoryKey}:`, 
        Object.fromEntries([...combosByType].map(([k, v]) => [k, Array.from(v)])));
    });
  }

  /**
   * Extract waist×inseam combination string from variant data
   * @param {Object} variant - Variant object with options/title
   * @returns {string|null} - Combination string like "32x30" or null
   */
  extractWaistInseamFromVariant(variant) {
    // Check variant options first
    if (variant.options && Array.isArray(variant.options)) {
      for (const opt of variant.options) {
        const optStr = String(opt);
        // Match patterns like "32x30", "32 x 30", etc.
        const match = optStr.match(/^(\d+)\s*x\s*(\d+)$/i);
        if (match) {
          return `${match[1]}x${match[2]}`;
        }
      }
    }
    
    // Fallback to variant title
    if (variant.title) {
      const match = variant.title.match(/(\d+)\s*x\s*(\d+)/i);
      if (match) {
        return `${match[1]}x${match[2]}`;
      }
    }
    
    return null;
  }

  /**
   * Get valid inseam options for a given waist selection
   * @param {string} categoryKey - The category key
   * @param {string} waist - The selected waist value
   * @returns {Array<string>} - Array of valid inseam values
   */
  getValidInseamsForWaist(categoryKey, waist) {
    const validCombos = this.validCombinations.get(categoryKey);
    if (!validCombos) return [];
    
    const validInseams = [];
    validCombos.forEach(combo => {
      const [w, i] = combo.split('x');
      if (w === waist) {
        validInseams.push(i);
      }
    });
    
    return validInseams;
  }

  /**
   * Get valid waist options for a given inseam selection
   * @param {string} categoryKey - The category key
   * @param {string} inseam - The selected inseam value
   * @returns {Array<string>} - Array of valid waist values
   */
  getValidWaistsForInseam(categoryKey, inseam) {
    const validCombos = this.validCombinations.get(categoryKey);
    if (!validCombos) return [];
    
    const validWaists = [];
    validCombos.forEach(combo => {
      const [w, i] = combo.split('x');
      if (i === inseam) {
        validWaists.push(w);
      }
    });
    
    return validWaists;
  }

  /**
   * Check if a waist×inseam combination is valid for a category
   * @param {string} categoryKey - The category key
   * @param {string} waist - Waist value
   * @param {string} inseam - Inseam value
   * @returns {boolean}
   */
  isValidCombination(categoryKey, waist, inseam) {
    const validCombos = this.validCombinations.get(categoryKey);
    if (!validCombos) return true; // No validation data, assume valid
    
    return validCombos.has(`${waist}x${inseam}`);
  }

  /**
   * Get valid waist×inseam combinations for a given type
   * @param {string} categoryKey - The category key
   * @param {string} type - The selected type (fit)
   * @returns {Set<string>} - Set of valid combinations like '32x30'
   */
  getValidCombinationsForType(categoryKey, type) {
    const combosByType = this.validCombinationsByType.get(categoryKey);
    if (!combosByType || !type) return new Set();
    
    return combosByType.get(type) || new Set();
  }

  /**
   * Get valid inseams for a given type and waist
   * @param {string} categoryKey - The category key
   * @param {string} type - The selected type (fit)
   * @param {string} waist - The selected waist value
   * @returns {Array<string>} - Array of valid inseam values
   */
  getValidInseamsForTypeAndWaist(categoryKey, type, waist) {
    const typeCombos = this.getValidCombinationsForType(categoryKey, type);
    if (typeCombos.size === 0) {
      // Fall back to all valid inseams for this waist (no type filter)
      return this.getValidInseamsForWaist(categoryKey, waist);
    }
    
    const validInseams = [];
    typeCombos.forEach(combo => {
      const [w, i] = combo.split('x');
      if (w === waist) {
        validInseams.push(i);
      }
    });
    
    return validInseams;
  }

  /**
   * Get valid waists for a given type and inseam
   * @param {string} categoryKey - The category key
   * @param {string} type - The selected type (fit)
   * @param {string} inseam - The selected inseam value
   * @returns {Array<string>} - Array of valid waist values
   */
  getValidWaistsForTypeAndInseam(categoryKey, type, inseam) {
    const typeCombos = this.getValidCombinationsForType(categoryKey, type);
    if (typeCombos.size === 0) {
      // Fall back to all valid waists for this inseam (no type filter)
      return this.getValidWaistsForInseam(categoryKey, inseam);
    }
    
    const validWaists = [];
    typeCombos.forEach(combo => {
      const [w, i] = combo.split('x');
      if (i === inseam) {
        validWaists.push(w);
      }
    });
    
    return validWaists;
  }

  /**
   * Get valid types for a given waist×inseam combination
   * @param {string} categoryKey - The category key
   * @param {string} waist - The selected waist value
   * @param {string} inseam - The selected inseam value
   * @returns {Array<string>} - Array of valid type values
   */
  getValidTypesForWaistInseam(categoryKey, waist, inseam) {
    const combosByType = this.validCombinationsByType.get(categoryKey);
    if (!combosByType) return [];
    
    const targetCombo = `${waist}x${inseam}`;
    const validTypes = [];
    
    combosByType.forEach((combos, type) => {
      if (combos.has(targetCombo)) {
        validTypes.push(type);
      }
    });
    
    return validTypes;
  }

  /**
   * Check if a type has a specific waist available
   * @param {string} categoryKey - The category key
   * @param {string} type - The type to check
   * @param {string} waist - The waist value to check
   * @returns {boolean}
   */
  typeHasWaist(categoryKey, type, waist) {
    const typeCombos = this.getValidCombinationsForType(categoryKey, type);
    for (const combo of typeCombos) {
      const [w] = combo.split('x');
      if (w === waist) return true;
    }
    return false;
  }

  /**
   * Check if a type has a specific inseam available
   * @param {string} categoryKey - The category key
   * @param {string} type - The type to check
   * @param {string} inseam - The inseam value to check
   * @returns {boolean}
   */
  typeHasInseam(categoryKey, type, inseam) {
    const typeCombos = this.getValidCombinationsForType(categoryKey, type);
    for (const combo of typeCombos) {
      const [, i] = combo.split('x');
      if (i === inseam) return true;
    }
    return false;
  }

  /**
   * Update visual availability state for waist/inseam options
   * Marks incompatible options as unavailable based on current selection (including type)
   * @param {string} categoryKey - The category key to update
   */
  updateWaistInseamAvailability(categoryKey) {
    const group = this.categoryGroups.get(categoryKey);
    if (!group || group.sizingType !== 'waist-inseam') return;
    
    const selection = this.selections.get(categoryKey);
    if (!selection) return;
    
    const waistInputs = this.querySelectorAll(`input[name="${categoryKey}-waist"]`);
    const inseamInputs = this.querySelectorAll(`input[name="${categoryKey}-inseam"]`);
    const hasTypeSelection = selection.type && group.types.length > 1;
    
    // If waist is selected (or type is selected), update inseam availability
    if (selection.waist || hasTypeSelection) {
      // Get valid inseams considering both type and waist
      let validInseams;
      if (hasTypeSelection && selection.waist) {
        validInseams = this.getValidInseamsForTypeAndWaist(categoryKey, selection.type, selection.waist);
      } else if (hasTypeSelection) {
        // Only type selected - get all inseams for this type
        const typeCombos = this.getValidCombinationsForType(categoryKey, selection.type);
        validInseams = [...new Set([...typeCombos].map(c => c.split('x')[1]))];
      } else {
        validInseams = this.getValidInseamsForWaist(categoryKey, selection.waist);
      }
      
      inseamInputs.forEach(input => {
        const isValid = validInseams.includes(input.value);
        const optionWrapper = input.closest('.bundle-product__option');
        const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
        
        // Visual state
        label?.classList.toggle('unavailable-combo', !isValid);
        optionWrapper?.classList.toggle('unavailable-combo', !isValid);
        
        // Accessibility: aria-disabled (not disabled - keeps it interactive for override)
        input.setAttribute('aria-disabled', !isValid ? 'true' : 'false');
        
        // Tooltip for sighted users
        if (!isValid) {
          const reason = hasTypeSelection && selection.waist 
            ? 'Not available with current fit and waist.'
            : hasTypeSelection 
            ? 'Not available with current fit.'
            : 'Not available with current waist.';
          label?.setAttribute('title', `${reason} Click to change.`);
        } else {
          label?.removeAttribute('title');
        }
      });
    } else {
      // No waist or type selected - clear all inseam unavailable states
      inseamInputs.forEach(input => {
        const optionWrapper = input.closest('.bundle-product__option');
        const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
        
        label?.classList.remove('unavailable-combo');
        optionWrapper?.classList.remove('unavailable-combo');
        input.setAttribute('aria-disabled', 'false');
        label?.removeAttribute('title');
      });
    }
    
    // If inseam is selected (or type is selected), update waist availability
    if (selection.inseam || hasTypeSelection) {
      // Get valid waists considering both type and inseam
      let validWaists;
      if (hasTypeSelection && selection.inseam) {
        validWaists = this.getValidWaistsForTypeAndInseam(categoryKey, selection.type, selection.inseam);
      } else if (hasTypeSelection) {
        // Only type selected - get all waists for this type
        const typeCombos = this.getValidCombinationsForType(categoryKey, selection.type);
        validWaists = [...new Set([...typeCombos].map(c => c.split('x')[0]))];
      } else {
        validWaists = this.getValidWaistsForInseam(categoryKey, selection.inseam);
      }
      
      waistInputs.forEach(input => {
        const isValid = validWaists.includes(input.value);
        const optionWrapper = input.closest('.bundle-product__option');
        const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
        
        // Visual state
        label?.classList.toggle('unavailable-combo', !isValid);
        optionWrapper?.classList.toggle('unavailable-combo', !isValid);
        
        // Accessibility
        input.setAttribute('aria-disabled', !isValid ? 'true' : 'false');
        
        // Tooltip
        if (!isValid) {
          const reason = hasTypeSelection && selection.inseam 
            ? 'Not available with current fit and inseam.'
            : hasTypeSelection 
            ? 'Not available with current fit.'
            : 'Not available with current inseam.';
          label?.setAttribute('title', `${reason} Click to change.`);
        } else {
          label?.removeAttribute('title');
        }
      });
    } else {
      // No inseam or type selected - clear all waist unavailable states
      waistInputs.forEach(input => {
        const optionWrapper = input.closest('.bundle-product__option');
        const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
        
        label?.classList.remove('unavailable-combo');
        optionWrapper?.classList.remove('unavailable-combo');
        input.setAttribute('aria-disabled', 'false');
        label?.removeAttribute('title');
      });
    }
  }

  /**
   * Update availability states for all waist-inseam categories
   */
  updateAllWaistInseamAvailability() {
    this.categoryGroups.forEach((group, categoryKey) => {
      if (group.sizingType === 'waist-inseam') {
        this.updateWaistInseamAvailability(categoryKey);
      }
    });
  }

  /**
   * Update visual availability state for type options
   * Marks types as unavailable if they don't have the selected waist/inseam combo
   * @param {string} categoryKey - The category key to update
   */
  updateTypeAvailability(categoryKey) {
    const group = this.categoryGroups.get(categoryKey);
    if (!group || group.sizingType !== 'waist-inseam') return;
    
    // Only process if there are multiple types
    if (!group.types || group.types.length <= 1) return;
    
    const selection = this.selections.get(categoryKey);
    if (!selection) return;
    
    const typeInputs = this.querySelectorAll(`input[name="${categoryKey}-type"]`);
    
    // If neither waist nor inseam is selected, clear all type unavailable states
    if (!selection.waist && !selection.inseam) {
      typeInputs.forEach(input => {
        const optionWrapper = input.closest('.bundle-product__option');
        const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
        
        label?.classList.remove('unavailable-combo');
        optionWrapper?.classList.remove('unavailable-combo');
        input.setAttribute('aria-disabled', 'false');
        label?.removeAttribute('title');
      });
      return;
    }
    
    // Determine which types are valid based on waist and/or inseam selection
    typeInputs.forEach(input => {
      const typeValue = input.value;
      let isValid = true;
      let reason = '';
      
      if (selection.waist && selection.inseam) {
        // Both waist and inseam selected - type must have this exact combo
        const validTypes = this.getValidTypesForWaistInseam(categoryKey, selection.waist, selection.inseam);
        isValid = validTypes.includes(typeValue);
        reason = 'Not available with current waist and inseam.';
      } else if (selection.waist) {
        // Only waist selected - type must have this waist in at least one combo
        isValid = this.typeHasWaist(categoryKey, typeValue, selection.waist);
        reason = 'Not available with current waist.';
      } else if (selection.inseam) {
        // Only inseam selected - type must have this inseam in at least one combo
        isValid = this.typeHasInseam(categoryKey, typeValue, selection.inseam);
        reason = 'Not available with current inseam.';
      }
      
      const optionWrapper = input.closest('.bundle-product__option');
      const label = optionWrapper?.querySelector('label') || this.querySelector(`label[for="${input.id}"]`);
      
      // Visual state
      label?.classList.toggle('unavailable-combo', !isValid);
      optionWrapper?.classList.toggle('unavailable-combo', !isValid);
      
      // Accessibility
      input.setAttribute('aria-disabled', !isValid ? 'true' : 'false');
      
      // Tooltip
      if (!isValid) {
        label?.setAttribute('title', `${reason} Click to change.`);
      } else {
        label?.removeAttribute('title');
      }
    });
  }

  /**
   * Update type availability states for all waist-inseam categories
   */
  updateAllTypeAvailability() {
    this.categoryGroups.forEach((group, categoryKey) => {
      if (group.sizingType === 'waist-inseam' && group.types && group.types.length > 1) {
        this.updateTypeAvailability(categoryKey);
      }
    });
  }

  /**
   * Clear waist selection for a category (uncheck radio)
   * @param {string} categoryKey
   */
  clearWaistSelection(categoryKey) {
    const waistInputs = this.querySelectorAll(`input[name="${categoryKey}-waist"]:checked`);
    waistInputs.forEach(input => {
      input.checked = false;
    });
    
    const selection = this.selections.get(categoryKey);
    if (selection) {
      selection.waist = null;
    }
  }

  /**
   * Clear inseam selection for a category (uncheck radio)
   * @param {string} categoryKey
   */
  clearInseamSelection(categoryKey) {
    const inseamInputs = this.querySelectorAll(`input[name="${categoryKey}-inseam"]:checked`);
    inseamInputs.forEach(input => {
      input.checked = false;
    });
    
    const selection = this.selections.get(categoryKey);
    if (selection) {
      selection.inseam = null;
    }
  }

  /**
   * Clear type selection for a category (uncheck radio)
   * @param {string} categoryKey
   */
  clearTypeSelection(categoryKey) {
    const typeInputs = this.querySelectorAll(`input[name="${categoryKey}-type"]:checked`);
    typeInputs.forEach(input => {
      input.checked = false;
    });
    
    const selection = this.selections.get(categoryKey);
    if (selection) {
      selection.type = null;
    }
  }

  /**
   * Load saved selections from sessionStorage
   */
  loadSavedSelections() {
    try {
      const saved = sessionStorage.getItem(BundleVariantSelects.STORAGE_KEY);
      if (!saved) return;
      
      const data = JSON.parse(saved);
      const bundleProductId = this.getBundleProductId();
      
      // Check if we have saved data for this bundle product
      if (data.bundleProductId === bundleProductId && data.selections) {
        // Convert saved selections object to Map
        Object.entries(data.selections).forEach(([key, value]) => {
          this.selections.set(key, value);
        });
        
        // Pre-select the saved options in the DOM
        this.applySelectionsToDOM();
        
        debugLog('[BundleVariantSelects] Loaded saved selections:', Object.fromEntries(this.selections));
      }
    } catch (e) {
      debugError('[BundleVariantSelects] Error loading saved selections:', e);
    }
  }

  /**
   * Apply saved selections to DOM inputs
   */
  applySelectionsToDOM() {
    this.selections.forEach((selection, categoryKey) => {
      // Apply type selection
      if (selection.type) {
        const typeInput = this.querySelector(
          `input[name="${categoryKey}-type"][value="${selection.type}"]`
        );
        if (typeInput) typeInput.checked = true;
      }
      
      // Apply size selection (standard sizing)
      if (selection.size) {
        const sizeInput = this.querySelector(
          `input[name="${categoryKey}-size"][value="${selection.size}"]`
        );
        if (sizeInput) sizeInput.checked = true;
        
        // Also check for shoe select dropdown
        if (categoryKey === 'shoe') {
          const shoeSelect = this.querySelector('select[name="shoe-size"]');
          if (shoeSelect) shoeSelect.value = selection.size;
        }
      }
      
      // Apply waist/inseam selection (bottom sizing)
      if (selection.waist) {
        const waistInput = this.querySelector(
          `input[name="${categoryKey}-waist"][value="${selection.waist}"]`
        );
        if (waistInput) waistInput.checked = true;
      }
      
      if (selection.inseam) {
        const inseamInput = this.querySelector(
          `input[name="${categoryKey}-inseam"][value="${selection.inseam}"]`
        );
        if (inseamInput) inseamInput.checked = true;
      }
    });
  }

  /**
   * Initialize selections from pre-checked inputs
   */
  initializeSelections() {
    // Initialize selection objects for each category
    this.categoryGroups.forEach((group, categoryKey) => {
      if (!this.selections.has(categoryKey)) {
        if (group.sizingType === 'waist-inseam') {
          this.selections.set(categoryKey, { 
            type: group.types.length === 1 ? group.types[0] : null,
            waist: null, 
            inseam: null 
          });
        } else {
          this.selections.set(categoryKey, { 
            type: group.types.length === 1 ? group.types[0] : null,
            size: null 
          });
        }
      }
    });
    
    // Auto-select categories that only have a single option (fieldset not rendered)
    this.categoryGroups.forEach((group, categoryKey) => {
      const selection = this.selections.get(categoryKey);
      if (!selection) return;

      if (group.sizingType === 'waist-inseam') {
        if (group.waistOptions?.length === 1 && !selection.waist) {
          selection.waist = group.waistOptions[0];
          debugLog(`[BundleVariantSelects] Auto-selected single waist option for ${categoryKey}:`, selection.waist);
        }
        if (group.inseamOptions?.length === 1 && !selection.inseam) {
          selection.inseam = group.inseamOptions[0];
          debugLog(`[BundleVariantSelects] Auto-selected single inseam option for ${categoryKey}:`, selection.inseam);
        }
      } else {
        if (group.sizeOptions?.length === 1 && !selection.size) {
          selection.size = group.sizeOptions[0];
          debugLog(`[BundleVariantSelects] Auto-selected single size option for ${categoryKey}:`, selection.size);
        }
      }
    });

    // Initialize shoe selection if shoes exist
    if (this.shoeGroup && !this.selections.has('shoe')) {
      this.selections.set('shoe', { size: null });
    }
    
    // Read any pre-checked inputs
    const checkedInputs = this.querySelectorAll('input[type="radio"]:checked');
    checkedInputs.forEach(input => {
      this.updateSelectionFromInput(input);
    });
    
    // Read shoe select value
    const shoeSelect = this.querySelector('select[name="shoe-size"]');
    if (shoeSelect && shoeSelect.value) {
      this.updateSelectionFromInput(shoeSelect);
    }
    
    // After initial selections, find matching variants
    this.findMatchingVariants();
    
    // Update waist/inseam and type availability visuals based on loaded selections
    this.updateAllWaistInseamAvailability();
    this.updateAllTypeAvailability();
    
    // Dispatch initial state
    this.dispatchVariantsChangedEvent();
    
    // Dispatch initial type selections for bundle-items filtering
    this.dispatchInitialTypeSelections();
  }

  /**
   * Dispatch type selection events for all categories with selected types
   * Used on initialization to sync bundle-items with default/saved selections
   */
  dispatchInitialTypeSelections() {
    this.selections.forEach((selection, categoryKey) => {
      if (selection.type) {
        document.dispatchEvent(new CustomEvent('bundle:type-change', {
          detail: { categoryKey, selectedType: selection.type }
        }));
      }
    });
  }

  /**
   * Handle input change events
   * @param {Event} event 
   */
  handleChange(event) {
    const input = event.target;
    
    if (input.type !== 'radio' && input.tagName !== 'SELECT') {
      return;
    }
    
    debugLog('[BundleVariantSelects] Change:', input.name, '=', input.value);
    
    // Get category and option type
    const categoryKey = input.dataset.categoryKey || input.name.split('-').slice(0, -1).join('-');
    const optionType = input.dataset.optionType || input.name.split('-').pop();
    
    // Check if this is a waist/inseam/type option being clicked while marked unavailable
    if (optionType === 'waist' || optionType === 'inseam' || optionType === 'type') {
      const optionWrapper = input.closest('.bundle-product__option');
      const isClickingUnavailable = optionWrapper?.classList.contains('unavailable-combo');
      
      if (isClickingUnavailable) {
        debugLog('[BundleVariantSelects] Clicked unavailable option, clearing conflicting selection');
        
        // Clear the OTHER selection(s) that's causing this to be unavailable
        if (optionType === 'waist') {
          // User clicked unavailable waist → clear inseam and possibly type
          this.clearInseamSelection(categoryKey);
          this.clearTypeSelection(categoryKey);
        } else if (optionType === 'inseam') {
          // User clicked unavailable inseam → clear waist and possibly type
          this.clearWaistSelection(categoryKey);
          this.clearTypeSelection(categoryKey);
        } else if (optionType === 'type') {
          // User clicked unavailable type → clear waist and inseam
          this.clearWaistSelection(categoryKey);
          this.clearInseamSelection(categoryKey);
        }
      }
    }
    
    this.updateSelectionFromInput(input);
    
    // Update availability visuals after selection change
    if (optionType === 'waist' || optionType === 'inseam' || optionType === 'type') {
      this.updateWaistInseamAvailability(categoryKey);
      this.updateTypeAvailability(categoryKey);
    }
    
    this.findMatchingVariants();
    
    // Auto-save to sessionStorage on every change
    this.saveToLocalStorage();
    
    // Dispatch event for parent to know selections changed
    this.dispatchVariantsChangedEvent();
  }

  /**
   * Dispatch variants changed event
   */
  dispatchVariantsChangedEvent() {
    this.dispatchEvent(new CustomEvent('bundle:variants-changed', {
      detail: {
        selections: Object.fromEntries(this.selections),
        selectedVariants: Array.from(this.selectedVariants.entries()),
        isComplete: this.isSelectionComplete(),
        bundleData: this.getBundleData()
      },
      bubbles: true
    }));
  }

  /**
   * Update selection state from an input
   * @param {HTMLInputElement|HTMLSelectElement} input 
   */
  updateSelectionFromInput(input) {
    const name = input.name || '';
    const value = input.value;
    
    // Get category key and option type from input attributes or name
    let categoryKey = input.dataset.categoryKey;
    let optionType = input.dataset.optionType;
    
    // Parse from name if not in data attributes (e.g., "polo-size" → category=polo, option=size)
    if (!categoryKey && name.includes('-')) {
      const parts = name.split('-');
      optionType = parts.pop(); // Last part is option type
      categoryKey = parts.join('-'); // Rest is category key
    }
    
    if (!categoryKey) {
      debugWarn('[BundleVariantSelects] Could not determine category for input:', name);
      return;
    }
    
    // Ensure selection object exists for this category
    if (!this.selections.has(categoryKey)) {
      this.selections.set(categoryKey, {});
    }
    
    const selection = this.selections.get(categoryKey);
    
    // Update the appropriate field based on option type
    switch (optionType) {
      case 'type':
        selection.type = value;
        debugLog(`[BundleVariantSelects] Set ${categoryKey}.type = "${value}"`);
        
        // Dispatch type change event for bundle-items filtering
        document.dispatchEvent(new CustomEvent('bundle:type-change', {
          detail: { categoryKey, selectedType: value }
        }));
        break;
      case 'size':
        selection.size = value;
        debugLog(`[BundleVariantSelects] Set ${categoryKey}.size = "${value}"`);
        break;
      case 'waist':
        selection.waist = value;
        debugLog(`[BundleVariantSelects] Set ${categoryKey}.waist = "${value}"`);
        break;
      case 'inseam':
        selection.inseam = value;
        debugLog(`[BundleVariantSelects] Set ${categoryKey}.inseam = "${value}"`);
        break;
      default:
        debugWarn(`[BundleVariantSelects] Unknown option type: ${optionType}`);
    }
    
    this.selections.set(categoryKey, selection);
  }

  /**
   * Find matching variants for all products based on current selections.
   *
   * IMPORTANT: Each category group can contain MULTIPLE products from different
   * bundle item slots (itemNumbers). For example, 3 polo shirts in the same
   * "Shirts & Tops" category occupy item slots 1, 3, and 5. We must resolve
   * a variant for EACH item slot independently, not just one per category.
   *
   * Products are grouped by itemNumber within each category. If a single item
   * slot has multiple product types (e.g., Polo and Sweater options), the type
   * selector filters within that slot. Otherwise every product is included.
   */
  findMatchingVariants() {
    debugLog('[BundleVariantSelects] ========== FINDING MATCHING VARIANTS ==========');
    debugLog('[BundleVariantSelects] Current selections:', Object.fromEntries(this.selections));
    debugLog('[BundleVariantSelects] Category groups count:', this.categoryGroups.size);
    debugLog('[BundleVariantSelects] Shoe group:', this.shoeGroup ? 'yes' : 'no');
    
    if (this.categoryGroups.size === 0 && !this.shoeGroup) {
      debugError('[BundleVariantSelects] ❌ No category groups or shoe group! Cannot match variants.');
      debugError('[BundleVariantSelects] Make sure parseCategoryGroupsData() ran successfully.');
    }
    
    this.selectedVariants.clear();
    
    // Process each category group
    this.categoryGroups.forEach((group, categoryKey) => {
      const selection = this.selections.get(categoryKey);
      if (!selection) return;
      
      debugLog(`[BundleVariantSelects] Processing category: ${group.category} (${categoryKey})`);
      debugLog(`[BundleVariantSelects] Selection:`, selection);
      debugLog(`[BundleVariantSelects] Products in group: ${group.products?.length || 0}`);
      
      if (!group.products || group.products.length === 0) {
        debugLog(`[BundleVariantSelects] No products in category ${categoryKey}`);
        return;
      }
      
      // Group products by their itemNumber (bundle slot position).
      // Multiple products sharing the same itemNumber are type alternatives
      // for that single slot (user picks one via the type selector).
      // Products with DIFFERENT itemNumbers are distinct bundle items that
      // all need their own resolved variant.
      const productsByItem = new Map();
      group.products.forEach(product => {
        const itemNum = product.itemNumber;
        if (itemNum == null) {
          debugWarn(`[BundleVariantSelects] Product "${product.productTitle}" missing itemNumber, using productId as fallback`);
        }
        const key = itemNum != null ? itemNum : `pid-${product.productId}`;
        if (!productsByItem.has(key)) {
          productsByItem.set(key, []);
        }
        productsByItem.get(key).push(product);
      });
      
      debugLog(`[BundleVariantSelects] Unique item slots in ${categoryKey}: ${productsByItem.size}`);
      
      // For each item slot, find the target product and matching variant
      productsByItem.forEach((products, itemKey) => {
        // Apply type filter WITHIN this item slot (not across the whole category)
        let targetProduct;
        if (selection.type && products.length > 1) {
          targetProduct = products.find(p => p.type === selection.type) || products[0];
        } else {
          targetProduct = products[0];
        }
        
        debugLog(`[BundleVariantSelects] Item slot ${itemKey}: target "${targetProduct.productTitle}" (type: ${targetProduct.type})`);
        
        // Find the matching variant based on size selection
        const matchingVariant = this.findVariantForProduct(targetProduct, group.sizingType, selection);
        
        if (matchingVariant) {
          this.selectedVariants.set(itemKey, {
            variantId: matchingVariant.id,
            productId: targetProduct.productId,
            productHandle: targetProduct.productHandle,
            productTitle: targetProduct.productTitle,
            productVendor: targetProduct.productVendor || '',
            variantTitle: matchingVariant.title,
            price: matchingVariant.price,
            originalPrice: matchingVariant.price, // Preserved original price for full-price checkout
            compareAtPrice: matchingVariant.compareAtPrice || 0,
            available: matchingVariant.available,
            sku: matchingVariant.sku,
            itemNumber: targetProduct.itemNumber || null,
            category: group.categorySimple || group.category,
            categoryKey: categoryKey,
            sizingType: group.sizingType,
            type: targetProduct.type,
            // Prefer variant image over product featured image
            featuredImage: matchingVariant.image || targetProduct.featuredImage || null
          });
          debugLog(`[BundleVariantSelects] ✓ Matched variant for slot ${itemKey} "${targetProduct.productTitle}":`, matchingVariant.title);
        } else {
          const selectedSize = group.sizingType === 'waist-inseam'
            ? `${selection.waist}x${selection.inseam}`
            : selection.size;
          this.setNoSizeMatchPlaceholder(targetProduct, {
            itemNumber: targetProduct.itemNumber || null,
            category: group.categorySimple || group.category,
            categoryKey: categoryKey,
            sizingType: group.sizingType
          }, selectedSize);
        }
      });
    });
    
    // Process shoe group
    if (this.shoeGroup) {
      const shoeSelection = this.selections.get('shoe');
      if (shoeSelection?.size) {
        this.shoeGroup.products.forEach(product => {
          const matchingVariant = this.findVariantForProduct(product, 'shoe', shoeSelection);
          const itemKey = product.itemNumber != null ? product.itemNumber : `pid-${product.productId}`;
          
          if (matchingVariant) {
            this.selectedVariants.set(itemKey, {
              variantId: matchingVariant.id,
              productId: product.productId,
              productHandle: product.productHandle,
              productTitle: product.productTitle,
              productVendor: product.productVendor || '',
              variantTitle: matchingVariant.title,
              price: matchingVariant.price,
              originalPrice: matchingVariant.price, // Preserved original price for full-price checkout
              compareAtPrice: matchingVariant.compareAtPrice || 0,
              available: matchingVariant.available,
              sku: matchingVariant.sku,
              itemNumber: product.itemNumber || null,
              category: 'Shoes',
              categoryKey: 'shoe',
              sizingType: 'shoe',
              type: product.type,
              // Prefer variant image over product featured image
              featuredImage: matchingVariant.image || product.featuredImage || null
            });
            debugLog(`[BundleVariantSelects] ✓ Matched shoe variant:`, matchingVariant.title, `(itemNumber: ${product.itemNumber || 'N/A'})`);
          } else {
            this.setNoSizeMatchPlaceholder(product, {
              itemNumber: product.itemNumber || null,
              category: 'Shoes',
              categoryKey: 'shoe',
              sizingType: 'shoe'
            }, shoeSelection.size);
          }
        });
      }
    }
    
    debugLog('[BundleVariantSelects] ========== MATCHING COMPLETE ==========');
    debugLog('[BundleVariantSelects] Total matched variants:', this.selectedVariants.size);
  }

  /**
   * Create and store a placeholder entry for a product that has no matching variant
   * in the user's selected size. Marks the item as unavailable (available: false,
   * noSizeMatch: true) so the OOS/alternates modal can handle the swap.
   *
   * variantId is intentionally null — this is not a real selection. Downstream code
   * must key off noSizeMatch/available rather than treat this as a valid variant.
   *
   * @param {Object} product - The product with no size match
   * @param {Object} groupMeta - Group metadata: { itemNumber, category, categoryKey, sizingType }
   * @param {string} selectedSizeForLog - The user's selected size (for debug logging)
   */
  setNoSizeMatchPlaceholder(product, groupMeta, selectedSizeForLog) {
    const itemKey = (product.itemNumber != null ? product.itemNumber : groupMeta.itemNumber) ?? `pid-${product.productId}`;
    this.selectedVariants.set(itemKey, {
      variantId: null,
      productId: product.productId,
      productHandle: product.productHandle,
      productTitle: product.productTitle,
      productVendor: product.productVendor || '',
      variantTitle: 'N/A',
      price: 0,
      originalPrice: 0,
      compareAtPrice: 0,
      available: false,
      noSizeMatch: true,
      sku: '',
      itemNumber: product.itemNumber || groupMeta.itemNumber || null,
      category: groupMeta.category,
      categoryKey: groupMeta.categoryKey,
      sizingType: groupMeta.sizingType,
      type: product.type,
      featuredImage: product.featuredImage || null
    });
    debugLog(`[BundleVariantSelects] ✗ No matching variant for "${product.productTitle}" in size "${selectedSizeForLog}" — marked as unavailable for alternate swap`);
  }

  /**
   * Find the target product within a category based on type selection
   * @param {Object} group - Category group data
   * @param {Object} selection - User selection for this category
   * @returns {Object|null} Target product or null
   */
  findTargetProduct(group, selection) {
    if (!group.products || group.products.length === 0) {
      return null;
    }
    
    // If only one type or no type selection needed, return first product
    if (group.types.length <= 1 || !selection.type) {
      return group.products[0];
    }
    
    // Find product matching selected type
    return group.products.find(p => p.type === selection.type) || group.products[0];
  }

  /**
   * Find the matching variant for a product based on size selection
   * @param {Object} product - Product data
   * @param {string} sizingType - 'standard', 'waist-inseam', or 'shoe'
   * @param {Object} selection - User selection
   * @returns {Object|null} Matching variant or null
   */
  findVariantForProduct(product, sizingType, selection) {
    if (!product.variants || product.variants.length === 0) {
      return null;
    }
    
    // Debug: Log available variant sizes for this product
    const allOptions = product.variants.map(v => this.parseVariantOptions(v)).flat();
    debugLog(`[BundleVariantSelects] Available variant options for ${product.productTitle}:`, [...new Set(allOptions)]);
    debugLog(`[BundleVariantSelects] Looking for size: "${selection.size}" with SizeMapping available: ${!!window.SizeMapping}`);
    
    return product.variants.find(variant => {
      const variantOptions = this.parseVariantOptions(variant);
      
      switch (sizingType) {
        case 'standard':
        case 'shoe':
          if (!selection.size) return false;
          // Check for exact match OR equivalent match via SizeMapping
          return variantOptions.some(opt => {
            // Exact match (case-insensitive)
            if (opt.toLowerCase() === selection.size.toLowerCase()) return true;
            // Equivalence match via SizeMapping utility
            if (window.SizeMapping && window.SizeMapping.sizesMatch) {
              const matches = window.SizeMapping.sizesMatch(selection.size, opt);
              if (matches) {
                debugLog(`[BundleVariantSelects] SizeMapping: "${selection.size}" matches "${opt}"`);
              }
              return matches;
            }
            return false;
          });
          
        case 'waist-inseam':
          if (!selection.waist || !selection.inseam) return false;
          const bottomMatch = `${selection.waist}x${selection.inseam}`;
          return variantOptions.some(opt => 
            opt === bottomMatch || 
            (opt.includes(selection.waist) && opt.includes(selection.inseam))
          );
          
        default:
          return false;
      }
    });
  }

  /**
   * Parse variant options from variant data
   * @param {Object} variant 
   * @returns {Array<string>}
   */
  parseVariantOptions(variant) {
    const options = [];
    
    if (variant.options) {
      options.push(...variant.options.map(o => o.toString()));
    }
    
    if (variant.title && variant.title !== 'Default Title') {
      options.push(variant.title);
      if (variant.title.includes('x')) {
        const parts = variant.title.split('x');
        options.push(...parts.map(p => p.trim()));
      }
    }
    
    return options;
  }

  /**
   * Check if all required selections are made
   * @returns {boolean}
   */
  isSelectionComplete() {
    // Must have at least some categories or shoes to be complete
    const hasCategoriesOrShoes = this.categoryGroups.size > 0 || this.shoeGroup;
    if (!hasCategoriesOrShoes) {
      debugLog('[BundleVariantSelects] isSelectionComplete: false (no category data)');
      return false;
    }
    
    // Check each category group
    for (const [categoryKey, group] of this.categoryGroups) {
      const selection = this.selections.get(categoryKey);
      if (!selection) {
        debugLog(`[BundleVariantSelects] isSelectionComplete: false (no selection for ${categoryKey})`);
        return false;
      }
      
      // Check type selection if multiple types exist
      if (group.types.length > 1 && !selection.type) {
        debugLog(`[BundleVariantSelects] isSelectionComplete: false (no type for ${categoryKey})`);
        return false;
      }
      
      // Check size selection based on sizing type
      if (group.sizingType === 'waist-inseam') {
        if (!selection.waist || !selection.inseam) {
          debugLog(`[BundleVariantSelects] isSelectionComplete: false (missing waist/inseam for ${categoryKey})`);
          return false;
        }
      } else {
        if (!selection.size) {
          debugLog(`[BundleVariantSelects] isSelectionComplete: false (no size for ${categoryKey})`);
          return false;
        }
      }
    }
    
    // Check shoe selection if shoes exist
    if (this.shoeGroup) {
      const shoeSelection = this.selections.get('shoe');
      if (!shoeSelection?.size) {
        debugLog('[BundleVariantSelects] isSelectionComplete: false (no shoe size)');
        return false;
      }
    }
    
    // Verify we have variants for all expected products
    const expectedProductCount = this.getExpectedProductCount();
    const hasEnoughVariants = this.selectedVariants.size >= expectedProductCount && expectedProductCount > 0;
    
    if (!hasEnoughVariants) {
      debugLog(`[BundleVariantSelects] isSelectionComplete: false (variants: ${this.selectedVariants.size}, expected: ${expectedProductCount})`);
    }
    
    return hasEnoughVariants;
  }

  /**
   * Get the expected number of products that should have variants selected.
   * Counts unique item slots (itemNumbers) across all category groups, since
   * multiple products in the same category but different slots each need a variant.
   * @returns {number}
   */
  getExpectedProductCount() {
    let count = 0;
    
    // Count unique item slots across all category groups
    this.categoryGroups.forEach((group) => {
      if (group.products && group.products.length > 0) {
        // Count unique itemNumbers in this group
        const itemNumbers = new Set(group.products.map(p => p.itemNumber).filter(n => n != null));
        // Use Set size if we have itemNumbers, otherwise fall back to 1 per category
        count += itemNumbers.size || 1;
      } else {
        count += 1;
      }
    });
    
    // Add shoe products
    if (this.shoeGroup) {
      count += this.shoeGroup.products.length;
    }
    
    return count;
  }

  // ==========================================================================
  // OOS DETECTION METHODS (for Alternates Modal)
  // ==========================================================================

  /**
   * Check which bundle items have OOS variants based on current selections
   * @returns {Array<Object>} Array of OOS items with their details
   */
  getOOSItems() {
    const oosItems = [];
    
    this.selectedVariants.forEach((variantData, productId) => {
      // Check if the matched variant is unavailable
      if (variantData && variantData.available === false) {
        // Get selected size for display
        const selectedSize = this.getSelectedSizeForCategory(variantData.categoryKey, variantData.sizingType);
        let displaySize = selectedSize;
        if (typeof selectedSize === 'object' && selectedSize.combined) {
          displaySize = selectedSize.combined;
        }
        
        oosItems.push({
          productId: variantData.productId,
          productTitle: variantData.productTitle,
          productHandle: variantData.productHandle,
          productVendor: variantData.productVendor || '',
          variantId: variantData.variantId,
          variantTitle: variantData.variantTitle,
          itemNumber: variantData.itemNumber || null, // NEW: Bundle slot position (1-6) for filtering alternates
          category: variantData.category,
          categoryKey: variantData.categoryKey,
          sizingType: variantData.sizingType,
          type: variantData.type,
          selectedSize: displaySize,
          featuredImage: variantData.featuredImage
        });
      }
    });
    
    debugLog('[BundleVariantSelects] OOS items found:', oosItems.length, oosItems);
    return oosItems;
  }

  /**
   * Get the user's selected size for a specific category
   * @param {string} categoryKey - The category key
   * @param {string} sizingType - 'standard', 'waist-inseam', or 'shoe'
   * @returns {string|Object} The selected size value(s)
   */
  getSelectedSizeForCategory(categoryKey, sizingType) {
    const selection = this.selections.get(categoryKey);
    if (!selection) return null;
    
    switch (sizingType) {
      case 'standard':
      case 'shoe':
        return selection.size;
      case 'waist-inseam':
        return {
          waist: selection.waist,
          inseam: selection.inseam,
          combined: `${selection.waist}x${selection.inseam}`
        };
      default:
        return selection.size || null;
    }
  }

  /**
   * Check if any selected variants are OOS
   * @returns {boolean}
   */
  hasOOSItems() {
    return this.getOOSItems().length > 0;
  }

  /**
   * Trigger the alternates modal for OOS items
   * Called when user has completed selections and OOS items exist
   * @returns {boolean} True if modal was triggered, false otherwise
   */
  triggerAlternatesModal() {
    const oosItems = this.getOOSItems();
    
    if (oosItems.length === 0) {
      debugLog('[BundleVariantSelects] No OOS items, skipping modal');
      return false;
    }
    
    debugLog('[BundleVariantSelects] Triggering alternates modal for', oosItems.length, 'items');
    
    // Get the manager and queue the swaps
    const manager = window.BundleAlternatesManager?.getInstance();
    
    if (manager) {
      manager.queueSwaps(oosItems);
      return true;
    } else {
      debugError('[BundleVariantSelects] BundleAlternatesManager not found. Make sure bundle-alternates.js is loaded.');
      return false;
    }
  }

  /**
   * Handle a confirmed swap - update our selectedVariants map
   * @param {Object} original - Original item data
   * @param {Object} replacement - Replacement item data
   */
  handleSwapConfirmed(original, replacement) {
    debugLog('[BundleVariantSelects] Handling swap confirmed:', original.productId, '→', replacement.productId);
    debugLog('[BundleVariantSelects] Original itemNumber:', original.itemNumber);
    
    // Update our selected variants map with the replacement
    // Key by itemNumber (bundle slot position) to preserve slot identity
    // IMPORTANT: Preserve itemNumber for checkout page swap filtering
    const swapKey = original.itemNumber ?? `pid-${original.productId}`;
    this.selectedVariants.set(swapKey, {
      variantId: replacement.variantId,
      productId: replacement.productId,
      productHandle: replacement.productHandle,
      productTitle: replacement.productTitle,
      productVendor: replacement.productVendor || '',
      variantTitle: replacement.variantTitle,
      price: replacement.price,
      originalPrice: replacement.price, // Preserved original price for full-price checkout
      compareAtPrice: replacement.compareAtPrice || 0,
      available: true, // The replacement should be available
      itemNumber: original.itemNumber, // CRITICAL: Preserve bundle slot position for checkout swaps
      category: original.category,
      categoryKey: original.categoryKey,
      sizingType: original.sizingType,
      type: replacement.type || original.type,
      featuredImage: replacement.featuredImage || null,
      isSwapped: true,
      originalProductId: original.productId,
      originalProductTitle: original.productTitle
    });
  }

  /**
   * Handle all swaps complete
   * @param {Array} swaps - Array of completed swaps
   */
  handleSwapsComplete(swaps) {
    debugLog('[BundleVariantSelects] All swaps complete:', swaps.length, 'swaps made');
    
    // Re-save to storage with updated variants
    this.saveToLocalStorage();
    
    // Dispatch updated event
    this.dispatchVariantsChangedEvent();
  }

  // ==========================================================================
  // DEBUG/TESTING HELPERS
  // ==========================================================================

  /**
   * Force a bundle item to appear as OOS for testing the alternates modal
   * Call from console: document.querySelector('bundle-variant-selects').forceOOS()
   * 
   * @param {number} index - Index of the bundle item to force OOS (0-based). 
   *                         If not provided, forces the first item OOS.
   * @returns {Object|null} The item that was forced OOS, or null if failed
   */
  forceOOS(index = 0) {
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Forcing item', index, 'to OOS');
    
    // Get the product ID at the given index
    const productIds = Array.from(this.selectedVariants.keys());
    
    if (productIds.length === 0) {
      debugError('[BundleVariantSelects] 🧪 DEBUG: No variants selected yet. Select sizes first.');
      return null;
    }
    
    if (index >= productIds.length) {
      debugError('[BundleVariantSelects] 🧪 DEBUG: Index', index, 'out of range. Max index:', productIds.length - 1);
      return null;
    }
    
    const productId = productIds[index];
    const variantData = this.selectedVariants.get(productId);
    
    if (!variantData) {
      debugError('[BundleVariantSelects] 🧪 DEBUG: No variant data for product', productId);
      return null;
    }
    
    // Force it to be unavailable
    variantData.available = false;
    this.selectedVariants.set(productId, variantData);
    
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Forced OOS:', {
      productId,
      productTitle: variantData.productTitle,
      variantTitle: variantData.variantTitle,
      itemType: variantData.itemType
    });
    
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Now click the CTA button to trigger the modal!');
    
    return variantData;
  }

  /**
   * Force ALL bundle items to appear as OOS (test multiple swaps)
   * Call from console: document.querySelector('bundle-variant-selects').forceAllOOS()
   */
  forceAllOOS() {
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Forcing ALL items to OOS');
    
    let count = 0;
    this.selectedVariants.forEach((variantData, productId) => {
      variantData.available = false;
      this.selectedVariants.set(productId, variantData);
      count++;
      debugLog('[BundleVariantSelects] 🧪 DEBUG: Forced OOS:', variantData.productTitle);
    });
    
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Forced', count, 'items to OOS');
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Now click the CTA button to trigger the modal!');
    
    return count;
  }

  /**
   * Reset all items to their actual availability
   * Call from console: document.querySelector('bundle-variant-selects').resetAvailability()
   */
  resetAvailability() {
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Resetting availability (re-matching variants)');
    this.findMatchingVariants();
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Availability reset complete');
  }

  /**
   * Directly trigger the alternates modal for testing (bypasses OOS check)
   * Call from console: document.querySelector('bundle-variant-selects').testModal()
   */
  testModal() {
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Directly triggering modal for testing');
    
    // Create a fake OOS item for testing
    const firstVariant = this.selectedVariants.values().next().value;
    
    if (!firstVariant) {
      debugError('[BundleVariantSelects] 🧪 DEBUG: No variants selected. Select sizes first.');
      return false;
    }
    
    const testItem = {
      productId: firstVariant.productId,
      productTitle: firstVariant.productTitle,
      productHandle: firstVariant.productHandle,
      variantId: firstVariant.variantId,
      variantTitle: firstVariant.variantTitle,
      itemNumber: firstVariant.itemNumber || null, // NEW: Bundle slot position (1-6)
      category: firstVariant.category,
      categoryKey: firstVariant.categoryKey,
      sizingType: firstVariant.sizingType,
      type: firstVariant.type,
      selectedSize: this.getSelectedSizeForCategory(firstVariant.categoryKey, firstVariant.sizingType),
      featuredImage: firstVariant.featuredImage
    };
    
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Test item:', testItem);
    
    const manager = window.BundleAlternatesManager?.getInstance();
    if (manager) {
      manager.queueSwaps([testItem]);
      return true;
    } else {
      debugError('[BundleVariantSelects] 🧪 DEBUG: BundleAlternatesManager not found');
      return false;
    }
  }

  /**
   * Log current state for debugging
   * Call from console: document.querySelector('bundle-variant-selects').debugState()
   */
  debugState() {
    debugLog('[BundleVariantSelects] 🧪 DEBUG: Current State');
    debugLog('─'.repeat(50));
    debugLog('Selections:', Object.fromEntries(this.selections));
    debugLog('Selection Complete:', this.isSelectionComplete());
    debugLog('Has OOS Items:', this.hasOOSItems());
    debugLog('─'.repeat(50));
    debugLog('Category Groups:');
    this.categoryGroups.forEach((group, key) => {
      debugLog(`  [${key}] ${group.category} (${group.sizingType})`);
      debugLog(`    Types: ${group.types.join(', ')}`);
      debugLog(`    Products: ${group.products.map(p => p.productTitle).join(', ')}`);
    });
    if (this.shoeGroup) {
      debugLog('  [shoe] Shoes');
      debugLog(`    Products: ${this.shoeGroup.products.map(p => p.productTitle).join(', ')}`);
    }
    debugLog('─'.repeat(50));
    debugLog('Selected Variants:');
    this.selectedVariants.forEach((data, productId) => {
      debugLog(`  ${data.productTitle}:`, {
        variantId: data.variantId,
        title: data.variantTitle,
        available: data.available,
        itemNumber: data.itemNumber || 'N/A',
        category: data.category,
        type: data.type
      });
    });
    debugLog('─'.repeat(50));
    return {
      selections: Object.fromEntries(this.selections),
      isComplete: this.isSelectionComplete(),
      hasOOS: this.hasOOSItems(),
      oosItems: this.getOOSItems()
    };
  }

  /**
   * Get the bundle product's variant ID
   * @returns {string|null}
   */
  getBundleVariantId() {
    const bundleProduct = this.closest('bundle-product');
    
    // Try multiple sources for the variant ID
    // 1. Data attribute on bundle-product element
    if (bundleProduct?.dataset.variantId) {
      return bundleProduct.dataset.variantId;
    }
    
    // 2. Hidden input in the form
    const variantInput = bundleProduct?.querySelector('input[name="id"], input[name="variant_id"]');
    if (variantInput?.value) {
      return variantInput.value;
    }
    
    // 3. URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return variantParam;
    }
    
    // 4. Data attribute for selected variant
    if (bundleProduct?.dataset.selectedVariantId) {
      return bundleProduct.dataset.selectedVariantId;
    }
    
    return null;
  }

  /**
   * Get complete bundle data for sessionStorage
   * @returns {Object}
   */
  getBundleData() {
    const bundleProduct = this.closest('bundle-product');
    const bundleProductId = this.getBundleProductId();
    const bundleVariantId = this.getBundleVariantId();
    
    // Get bundle product's pricing (main bundle product price, not the plan)
    const bundlePrice = bundleProduct?.dataset.bundlePrice 
      ? parseInt(bundleProduct.dataset.bundlePrice, 10) 
      : null;
    const bundleCompareAtPrice = bundleProduct?.dataset.bundleCompareAtPrice 
      ? parseInt(bundleProduct.dataset.bundleCompareAtPrice, 10) 
      : null;
    
    // Get bundle product's own selling plan (Recharge subscription on the bundle itself)
    const bundleSellingPlanId = bundleProduct?.dataset.bundleSellingPlanId || null;
    
    // Get plan product data from bundle-product data attributes (Regal tier info)
    const planData = this.getPlanData(bundleProduct);
    
    // Convert selections Map to plain object for JSON serialization
    const selectionsObject = Object.fromEntries(this.selections);
    
    // Build category info for reference
    const categoryInfo = [];
    this.categoryGroups.forEach((group, key) => {
      categoryInfo.push({
        categoryKey: key,
        category: group.categorySimple || group.category,
        sizingType: group.sizingType,
        types: group.types,
        hasMultipleTypes: group.types.length > 1
      });
    });
    
    return {
      // Bundle product info
      bundleProductId: bundleProductId,
      bundleVariantId: bundleVariantId,
      bundleProductTitle: bundleProduct?.dataset.productTitle || null,
      bundleProductUrl: window.location.pathname,
      bundleProductHandle: bundleProduct?.dataset.productHandle || null,
      
      // Bundle product pricing (subscription price)
      bundlePrice: bundlePrice,
      bundleCompareAtPrice: bundleCompareAtPrice,
      
      // Bundle's own Recharge selling plan (for subscription checkout)
      bundleSellingPlanId: bundleSellingPlanId,
      
      // Plan/subscription product info (from bundle_look.plan metafield - tier info for cart properties)
      plan: planData,
      
      // User selections (by category)
      selections: selectionsObject,
      
      // Category structure info
      categories: categoryInfo,
      
      // Matched bundle item variants
      items: Array.from(this.selectedVariants.values()),
      
      // Status
      isComplete: this.isSelectionComplete(),
      timestamp: Date.now()
    };
  }
  
  /**
   * Get plan product data from bundle-product element
   * The plan is stored in bundle_look.plan metafield and exposed via data attributes
   * 
   * @param {HTMLElement} bundleProduct - The bundle-product element
   * @returns {Object|null} Plan data or null if no plan exists
   */
  getPlanData(bundleProduct) {
    if (!bundleProduct) return null;
    
    const planProductId = bundleProduct.dataset.planProductId;
    const planProductTitle = bundleProduct.dataset.planProductTitle;
    const planProductHandle = bundleProduct.dataset.planProductHandle;
    const planVariantId = bundleProduct.dataset.planVariantId;
    const planVariantPrice = bundleProduct.dataset.planVariantPrice;
    const planVariantCompareAtPrice = bundleProduct.dataset.planVariantCompareAtPrice;
    const planSellingPlanId = bundleProduct.dataset.planSellingPlanId;
    
    // Return null if no plan product is set
    if (!planProductId) return null;
    
    // Extract tier name from the plan product title (e.g., "Gold Plan" → "Gold")
    const tierName = planProductTitle ? planProductTitle.split(' ')[0] : null;
    
    return {
      productId: planProductId,
      productTitle: planProductTitle,
      productHandle: planProductHandle,
      variantId: planVariantId,
      variantPrice: planVariantPrice ? parseInt(planVariantPrice, 10) : null,
      variantCompareAtPrice: planVariantCompareAtPrice ? parseInt(planVariantCompareAtPrice, 10) : null,
      sellingPlanId: planSellingPlanId,
      tierName: tierName
    };
  }

  /**
   * Save current selections to sessionStorage
   */
  saveToLocalStorage() {
    debugLog('[BundleVariantSelects] ========== SAVING TO sessionStorage ==========');
    try {
      const data = this.getBundleData();
      
      // Also include alternates data for use on checkout page
      const alternatesModal = document.querySelector('bundle-alternates-modal');
      if (alternatesModal?.alternatesData) {
        data.alternates = alternatesModal.alternatesData;
        debugLog('[BundleVariantSelects] Including alternates data:', data.alternates.length, 'items');
      }
      
      debugLog('[BundleVariantSelects] Data to save:', JSON.stringify(data, null, 2));
      debugLog('[BundleVariantSelects] Items count:', data.items?.length || 0);
      debugLog('[BundleVariantSelects] Is complete:', data.isComplete);
      
      sessionStorage.setItem(BundleVariantSelects.STORAGE_KEY, JSON.stringify(data));
      
      // Verify it was saved
      const verification = sessionStorage.getItem(BundleVariantSelects.STORAGE_KEY);
      debugLog('[BundleVariantSelects] ✓ Saved successfully. Storage key:', BundleVariantSelects.STORAGE_KEY);
      debugLog('[BundleVariantSelects] Verification - data in sessionStorage:', !!verification);
    } catch (e) {
      debugError('[BundleVariantSelects] ✗ Error saving to sessionStorage:', e);
    }
  }

  /**
   * Clear saved selections from sessionStorage
   */
  clearLocalStorage() {
    try {
      sessionStorage.removeItem(BundleVariantSelects.STORAGE_KEY);
      debugLog('[BundleVariantSelects] Cleared sessionStorage');
    } catch (e) {
      debugError('[BundleVariantSelects] Error clearing sessionStorage:', e);
    }
  }

  /**
   * Static method to get saved bundle data from sessionStorage
   * @returns {Object|null}
   */
  static getSavedBundleData() {
    try {
      const saved = sessionStorage.getItem(BundleVariantSelects.STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      debugError('[BundleVariantSelects] Error reading sessionStorage:', e);
      return null;
    }
  }

  /**
   * Static method to get cart-ready items from sessionStorage
   * @returns {Array<{id: string, quantity: number, properties: Object}>}
   */
  static getCartItemsFromStorage() {
    const data = BundleVariantSelects.getSavedBundleData();
    if (!data || !data.items) return [];
    
    return data.items.map(item => ({
      id: item.variantId,
      quantity: 1,
      properties: {
        '_bundle_product_id': data.bundleProductId,
        '_bundle_item_type': item.itemType
      }
    }));
  }
}


// ============================================================================
// BUNDLE CTA - Handles saving bundle selections to sessionStorage
// ============================================================================
class BundleCta extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.bundleData = null;
    this.handleClick = this.handleClick.bind(this);
    this.handleVariantsChanged = this.handleVariantsChanged.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button[type="submit"], .bundle-product__cta-button');
    
    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }
    
    // Listen for variant selection changes
    const bundleProduct = this.closest('bundle-product');
    if (bundleProduct) {
      bundleProduct.addEventListener('bundle:variants-changed', this.handleVariantsChanged);
    }
    
    // Check current state from variant selects (handles auto-loaded selections)
    this.checkInitialState();
  }
  
  /**
   * Check initial state from variant selects
   * This handles cases where selections were auto-loaded from sessionStorage
   * Uses requestAnimationFrame to ensure variant selects have fully initialized
   */
  checkInitialState() {
    // Use requestAnimationFrame to ensure all custom elements have connected and initialized
    requestAnimationFrame(() => {
      const variantSelects = this.closest('bundle-product')?.querySelector('bundle-variant-selects');
      if (variantSelects) {
        const bundleData = variantSelects.getBundleData();
        const isComplete = bundleData?.isComplete || false;
        this.updateButtonState(isComplete);
        this.bundleData = bundleData;
        debugLog('[BundleCta] Initial state check - isComplete:', isComplete);
      } else {
        // No variant selects found yet, disable button
        this.updateButtonState(false);
      }
    });
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
    
    const bundleProduct = this.closest('bundle-product');
    if (bundleProduct) {
      bundleProduct.removeEventListener('bundle:variants-changed', this.handleVariantsChanged);
    }
  }

  /**
   * Handle variant selection changes
   * @param {CustomEvent} event 
   */
  handleVariantsChanged(event) {
    const { isComplete, bundleData } = event.detail;
    this.updateButtonState(isComplete);
    this.bundleData = bundleData;
  }

  /**
   * Update button enabled/disabled state
   * @param {boolean} isComplete 
   */
  updateButtonState(isComplete) {
    if (this.button) {
      this.button.disabled = !isComplete;
      this.button.classList.toggle('is-disabled', !isComplete);
    }
  }

  /**
   * Handle confirm/save click
   * @param {Event} event 
   */
  handleClick(event) {
    event.preventDefault();
    
    const variantSelects = this.closest('bundle-product')?.querySelector('bundle-variant-selects');
    if (!variantSelects) {
      debugError('[BundleCta] Could not find bundle-variant-selects');
      return;
    }
    
    const bundleData = variantSelects.getBundleData();
    
    if (!bundleData.isComplete) {
      debugError('[BundleCta] Bundle selection is not complete');
      // Optionally dispatch an event or show UI feedback
      this.dispatchEvent(new CustomEvent('bundle:selections-incomplete', {
        detail: { bundleData },
        bubbles: true
      }));
      return;
    }
    
    // Check for OOS items and trigger modal if needed (Option B)
    if (variantSelects.hasOOSItems()) {
      debugLog('[BundleCta] OOS items detected, triggering alternates modal');
      
      const modalTriggered = variantSelects.triggerAlternatesModal();
      
      if (modalTriggered) {
        // Don't proceed yet - wait for swaps to complete
        // The modal will handle the flow and dispatch bundle:swaps-complete
        // We'll listen for that event to proceed
        this.waitForSwapsAndProceed(variantSelects);
        return;
      }
      // If modal not available, fall through and proceed anyway
      debugWarn('[BundleCta] Modal not available, proceeding with OOS items');
    }
    
    // No OOS items (or modal not available) - proceed normally
    this.proceedWithBundle(variantSelects, bundleData);
  }

  /**
   * Wait for swaps to complete, then proceed with bundle
   * @param {BundleVariantSelects} variantSelects 
   */
  waitForSwapsAndProceed(variantSelects) {
    const handleComplete = (event) => {
      document.removeEventListener('bundle:swaps-complete', handleComplete);
      document.removeEventListener('bundle:swaps-cancelled', handleCancelled);
      
      debugLog('[BundleCta] Swaps complete, proceeding with bundle');
      
      // Get fresh bundle data after swaps
      const bundleData = variantSelects.getBundleData();
      this.proceedWithBundle(variantSelects, bundleData);
    };
    
    const handleCancelled = () => {
      document.removeEventListener('bundle:swaps-complete', handleComplete);
      document.removeEventListener('bundle:swaps-cancelled', handleCancelled);
      
      debugLog('[BundleCta] Swaps cancelled, aborting');
      // User cancelled - don't proceed
    };
    
    document.addEventListener('bundle:swaps-complete', handleComplete);
    document.addEventListener('bundle:swaps-cancelled', handleCancelled);
  }

  /**
   * Proceed with the bundle after OOS handling is done
   * @param {BundleVariantSelects} variantSelects 
   * @param {Object} bundleData 
   */
  proceedWithBundle(variantSelects, bundleData) {
    // Save to sessionStorage
    variantSelects.saveToLocalStorage();
    
    debugLog('[BundleCta] Bundle selections confirmed:', bundleData);
    
    // Dispatch success event with the saved data
    this.dispatchEvent(new CustomEvent('bundle:selections-confirmed', {
      detail: { bundleData },
      bubbles: true
    }));
    
    // Handle next step (e.g., navigate to next page, show confirmation, etc.)
    this.handleConfirmSuccess(bundleData);
  }

  /**
   * Handle successful confirmation
   * @param {Object} bundleData 
   */
  handleConfirmSuccess(bundleData) {
    // Dispatch global event for other components to listen
    document.dispatchEvent(new CustomEvent('bundle:selections-saved', {
      detail: bundleData
    }));
    
    // If there's a next URL defined, navigate to it
    const nextUrl = this.dataset.nextUrl;
    if (nextUrl) {
      window.location.href = nextUrl;
    }
  }
}


// ============================================================================
// BUNDLE STORAGE HELPER - Static utility for accessing bundle data
// ============================================================================
const BundleStorage = {
  STORAGE_KEY: 'bundle_selections',

  /**
   * Get saved bundle data from sessionStorage
   * @returns {Object|null}
   */
  get() {
    try {
      const saved = sessionStorage.getItem(this.STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      debugError('[BundleStorage] Error reading:', e);
      return null;
    }
  },

  /**
   * Save bundle data to sessionStorage
   * @param {Object} data 
   */
  set(data) {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      debugError('[BundleStorage] Error saving:', e);
    }
  },

  /**
   * Clear bundle data from sessionStorage
   */
  clear() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      debugError('[BundleStorage] Error clearing:', e);
    }
  },

  /**
   * Check if bundle selections exist and are complete
   * @returns {boolean}
   */
  isComplete() {
    const data = this.get();
    return data?.isComplete === true;
  },

  /**
   * Get the bundle product variant ID
   * @returns {string|null}
   */
  getBundleVariantId() {
    const data = this.get();
    return data?.bundleVariantId || null;
  },

  /**
   * Get the bundle product ID
   * @returns {string|null}
   */
  getBundleProductId() {
    const data = this.get();
    return data?.bundleProductId || null;
  },

  /**
   * Get cart-ready items from storage (bundle item variants only)
   * @returns {Array<{id: string, quantity: number, properties: Object}>}
   */
  getCartItems() {
    const data = this.get();
    if (!data || !data.items) return [];
    
    return data.items.map(item => ({
      id: item.variantId,
      quantity: 1,
      properties: {
        '_bundle_product_id': data.bundleProductId,
        '_bundle_variant_id': data.bundleVariantId,
        '_bundle_item_type': item.itemType
      }
    }));
  },

  /**
   * Get cart-ready items including the bundle product itself
   * @returns {Array<{id: string, quantity: number, properties: Object}>}
   */
  getCartItemsWithBundle() {
    const data = this.get();
    if (!data) return [];
    
    const items = [];
    
    // Add the bundle product variant first (if available)
    if (data.bundleVariantId) {
      items.push({
        id: data.bundleVariantId,
        quantity: 1,
        properties: {
          '_is_bundle_parent': 'true',
          '_bundle_items_count': String(data.items?.length || 0)
        }
      });
    }
    
    // Add bundle item variants
    if (data.items) {
      data.items.forEach(item => {
        items.push({
          id: item.variantId,
          quantity: 1,
          properties: {
            '_bundle_product_id': data.bundleProductId,
            '_bundle_variant_id': data.bundleVariantId,
            '_bundle_item_type': item.itemType
          }
        });
      });
    }
    
    return items;
  },

  /**
   * Get only the bundle product as a cart item (without individual items)
   * @returns {Object|null}
   */
  getBundleProductCartItem() {
    const data = this.get();
    if (!data?.bundleVariantId) return null;
    
    // Build properties with selection details
    const properties = {
      '_is_bundle': 'true',
      '_bundle_items_count': String(data.items?.length || 0)
    };
    
    // Add selection details
    if (data.selections) {
      if (data.selections.top) properties['Top Size'] = data.selections.top;
      if (data.selections.waist) properties['Waist'] = data.selections.waist;
      if (data.selections.inseam) properties['Inseam'] = data.selections.inseam;
      if (data.selections.shoe) properties['Shoe Size'] = data.selections.shoe;
    }
    
    // Add selected item variant IDs as a comma-separated string
    if (data.items?.length) {
      properties['_bundle_item_variants'] = data.items.map(i => i.variantId).join(',');
    }
    
    return {
      id: data.bundleVariantId,
      quantity: 1,
      properties
    };
  },

  /**
   * Add bundle items to cart from storage
   * @param {Object} options - { includeBundleProduct: boolean }
   * @returns {Promise<Object>}
   */
  async addToCart(options = {}) {
    const { includeBundleProduct = false } = options;
    
    const items = includeBundleProduct 
      ? this.getCartItemsWithBundle() 
      : this.getCartItems();
    
    if (items.length === 0) {
      throw new Error('No bundle items to add to cart');
    }
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }
    
    // Clear storage after successful add
    this.clear();
    
    return response.json();
  },

  /**
   * Add only the bundle product to cart (with selection data in properties)
   * @returns {Promise<Object>}
   */
  async addBundleProductToCart() {
    const item = this.getBundleProductCartItem();
    
    if (!item) {
      throw new Error('No bundle product to add to cart');
    }
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [item] })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }
    
    // Clear storage after successful add
    this.clear();
    
    return response.json();
  }
};

// Make BundleStorage available globally
if (typeof window !== 'undefined') {
  window.BundleStorage = BundleStorage;
}


// ============================================================================
// BUNDLE ITEMS - Handles type-based filtering of bundle item cards
// ============================================================================
class BundleItems extends HTMLElement {
  constructor() {
    super();
    
    // Configuration from JSON
    this.config = {
      categoriesWithMultipleTypes: [],
      defaultTypes: {}
    };
    
    // Current type selections by category
    this.typeSelections = new Map();
    
    // Swiper instance reference
    this.swiper = null;
    
    // Bound event handlers
    this.handleTypeChange = this.handleTypeChange.bind(this);
  }

  connectedCallback() {
    debugLog('[BundleItems] Connected');
    
    // Parse configuration from JSON
    this.parseConfig();
    
    // Initialize type selections with defaults
    this.initializeDefaults();
    
    // Apply initial filtering
    this.filterItems();
    
    // Listen for type change events from variant picker
    document.addEventListener('bundle:type-change', this.handleTypeChange);
    
    // Initialize swiper carousel
    this.initSwiper();
  }

  disconnectedCallback() {
    document.removeEventListener('bundle:type-change', this.handleTypeChange);
    this.destroySwiper();
  }

  // ==========================================================================
  // Swiper Initialization
  // ==========================================================================

  /**
   * Initialize the Swiper carousel from data attributes on this element.
   * Safe to call multiple times — guards against double-init.
   */
  initSwiper() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) return;

    // Guard against double initialization
    if (swiperEl.swiper) {
      this.swiper = swiperEl.swiper;
      return;
    }

    const options = this.buildSwiperOptions();

    this.swiper = new Swiper(swiperEl, options);
    debugLog('[BundleItems] Swiper initialized', options);
  }

  /**
   * Destroy the current Swiper instance and release the reference.
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Build Swiper options from this element's data attributes.
   * Parses breakpoints, pagination, navigation, and observer settings.
   * @returns {Object} Swiper configuration object
   */
  buildSwiperOptions() {
    const data = this.dataset;

    const spv = parseFloat(data.slidesPerView);
    const sb = parseInt(data.spaceBetween, 10);
    const spg = parseInt(data.slidesPerGroup, 10);

    const options = {
      slidesPerView: Number.isNaN(spv) ? 1 : spv,
      spaceBetween: Number.isNaN(sb) ? 10 : sb,
      slidesPerGroup: Number.isNaN(spg) ? 1 : spg,
      speed: 300,
      grabCursor: true,
      a11y: true,
      observer: data.observer === 'true',
      observeParents: data.observer === 'true',
      resizeObserver: true
    };

    if (data.freeMode === 'true') {
      options.freeMode = true;
    }

    // Breakpoints
    if (data.breakpoints) {
      try {
        const breakpoints = JSON.parse(data.breakpoints);
        for (const bp of Object.keys(breakpoints)) {
          const conf = breakpoints[bp];
          if (conf.slidesPerView !== undefined && conf.slidesPerView !== 'auto') {
            conf.slidesPerView = parseFloat(conf.slidesPerView);
          }
          if (conf.spaceBetween !== undefined) {
            conf.spaceBetween = parseInt(conf.spaceBetween, 10);
          }
          if (conf.slidesPerGroup !== undefined) {
            conf.slidesPerGroup = parseInt(conf.slidesPerGroup, 10);
          }
          if (conf.slidesOffsetBefore !== undefined) {
            conf.slidesOffsetBefore = parseInt(conf.slidesOffsetBefore, 10);
          }
          if (conf.slidesOffsetAfter !== undefined) {
            conf.slidesOffsetAfter = parseInt(conf.slidesOffsetAfter, 10);
          }
        }
        options.breakpoints = breakpoints;
      } catch (e) {
        debugError('[BundleItems] Error parsing breakpoints:', e);
      }
    }

    // Pagination
    if (data.pagination !== 'false') {
      const paginationEl = this.querySelector('[class*="__pagination"]');
      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
          type: data.paginationType || 'progressbar'
        };
      }
    } else {
      options.pagination = false;
    }

    // Navigation
    if (data.navigation !== 'false') {
      const prevBtn = this.querySelector('[class*="__button--prev"]');
      const nextBtn = this.querySelector('[class*="__button--next"]');
      if (prevBtn && nextBtn) {
        options.navigation = { prevEl: prevBtn, nextEl: nextBtn };
      }
    } else {
      options.navigation = false;
    }

    return options;
  }

  /**
   * Parse configuration from JSON script tag
   */
  parseConfig() {
    const configEl = this.querySelector('script[data-type-filter-config]');
    if (!configEl) {
      debugWarn('[BundleItems] No config element found');
      return;
    }
    
    try {
      const data = JSON.parse(configEl.textContent || '{}');
      this.config.categoriesWithMultipleTypes = data.categoriesWithMultipleTypes || [];
      this.config.defaultTypes = data.defaultTypes || {};
      
      debugLog('[BundleItems] Config loaded:', this.config);
    } catch (e) {
      debugError('[BundleItems] Error parsing config:', e);
    }
  }

  /**
   * Initialize default type selections
   */
  initializeDefaults() {
    // Set defaults for categories with multiple types
    for (const [categoryKey, defaultType] of Object.entries(this.config.defaultTypes)) {
      this.typeSelections.set(categoryKey, defaultType);
    }
    
    debugLog('[BundleItems] Initial type selections:', Object.fromEntries(this.typeSelections));
  }

  /**
   * Handle type change event from variant picker
   * @param {CustomEvent} event 
   */
  handleTypeChange(event) {
    const { categoryKey, selectedType } = event.detail;
    
    debugLog(`[BundleItems] Type change: ${categoryKey} → ${selectedType}`);
    
    // Only track categories that have multiple types
    if (!this.config.categoriesWithMultipleTypes.includes(categoryKey)) {
      debugLog(`[BundleItems] Category ${categoryKey} doesn't have multiple types, ignoring`);
      return;
    }
    
    // Check if this is actually a change
    const currentType = this.typeSelections.get(categoryKey);
    if (currentType === selectedType) {
      debugLog(`[BundleItems] Type unchanged, skipping animation`);
      return;
    }
    
    // Update selection
    this.typeSelections.set(categoryKey, selectedType);
    
    // Filter items with animation
    this.filterItemsWithAnimation();
  }

  /**
   * Filter items based on current type selections (no animation, used on init)
   */
  filterItems() {
    const items = this.querySelectorAll('.bundle-items__item');
    let visibleCount = 0;
    
    items.forEach(item => {
      const category = item.dataset.productCategory;
      const type = item.dataset.productType;
      
      // Determine if this item should be visible
      let shouldShow = true;
      
      // If this category has multiple types, check if this type is selected
      if (this.config.categoriesWithMultipleTypes.includes(category)) {
        const selectedType = this.typeSelections.get(category);
        shouldShow = !selectedType || type === selectedType;
      }
      
      // Apply visibility
      if (shouldShow) {
        item.classList.remove('is-hidden-by-type');
        item.style.display = '';
        visibleCount++;
      } else {
        item.classList.add('is-hidden-by-type');
        item.style.display = 'none';
      }
    });
    
    debugLog(`[BundleItems] Filtered: ${visibleCount} visible items`);
    
    // Update swiper after filtering
    this.updateSwiper();
  }

  /**
   * Filter items with fade animation
   * Fades out → updates content → fades in → re-inits swiper
   */
  async filterItemsWithAnimation() {
    const container = this.querySelector('.bundle-items__container');
    if (!container) {
      // Fallback to non-animated filter
      this.filterItems();
      return;
    }
    
    const FADE_DURATION = 200; // ms
    
    // Step 1: Fade out
    container.style.transition = `opacity ${FADE_DURATION}ms ease`;
    container.style.opacity = '0';
    
    // Wait for fade out
    await new Promise(resolve => setTimeout(resolve, FADE_DURATION));
    
    // Step 2: Update content (show/hide items)
    const items = this.querySelectorAll('.bundle-items__item');
    let visibleCount = 0;
    
    items.forEach(item => {
      const category = item.dataset.productCategory;
      const type = item.dataset.productType;
      
      let shouldShow = true;
      
      if (this.config.categoriesWithMultipleTypes.includes(category)) {
        const selectedType = this.typeSelections.get(category);
        shouldShow = !selectedType || type === selectedType;
      }
      
      if (shouldShow) {
        item.classList.remove('is-hidden-by-type');
        item.style.display = '';
        visibleCount++;
      } else {
        item.classList.add('is-hidden-by-type');
        item.style.display = 'none';
      }
    });
    
    debugLog(`[BundleItems] Filtered with animation: ${visibleCount} visible items`);
    
    // Step 3: Update/reinit swiper
    this.updateSwiper();
    
    // Small delay to let swiper recalculate
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Step 4: Fade in
    container.style.opacity = '1';
    
    // Clean up transition style after animation completes
    setTimeout(() => {
      container.style.transition = '';
    }, FADE_DURATION);
  }

  /**
   * Update swiper after filtering items
   */
  updateSwiper() {
    // Get swiper instance
    if (!this.swiper) {
      this.swiper = this.querySelector('.swiper')?.swiper;
    }
    
    if (this.swiper) {
      // Swiper needs to recalculate after DOM changes
      this.swiper.update();
      this.swiper.slideTo(0, 0); // Reset to first slide
      debugLog('[BundleItems] Swiper updated');
    }
  }

  /**
   * Get currently visible items
   * @returns {NodeList}
   */
  getVisibleItems() {
    return this.querySelectorAll('.bundle-items__item:not(.is-hidden-by-type)');
  }
}


// ============================================================================
// REGISTER CUSTOM ELEMENTS
// ============================================================================
if (!customElements.get('bundle-product')) {
  customElements.define('bundle-product', BundleProduct);
}

if (!customElements.get('bundle-plan-picker')) {
  customElements.define('bundle-plan-picker', BundlePlanPicker);
}

if (!customElements.get('bundle-sibling-picker')) {
  customElements.define('bundle-sibling-picker', BundleSiblingPicker);
}

if (!customElements.get('bundle-gallery')) {
  customElements.define('bundle-gallery', BundleGallery);
}

if (!customElements.get('bundle-variant-selects')) {
  customElements.define('bundle-variant-selects', BundleVariantSelects);
}

if (!customElements.get('bundle-cta')) {
  customElements.define('bundle-cta', BundleCta);
}

if (!customElements.get('bundle-items')) {
  customElements.define('bundle-items', BundleItems);
}


// ============================================================================
// EXPORTS
// ============================================================================
export { 
  BundleProduct, 
  BundlePlanPicker, 
  BundleSiblingPicker, 
  BundleGallery,
  BundleVariantSelects,
  BundleCta,
  BundleItems,
  BundleStorage
};

